var _ = require('optimizely-client-fn');
var enums = require('./constants/enums');
var eventFilterTypes = require('./constants/event_filter_types');
var layerPolicies = require('./constants/layer_policies');
var TagEnums = require('./tags/enums');
var trackingPolicies = require('./constants/tracking_policies');

// Only import the schema module if invariant is set to avoid extending native prototypes in prod.
var schema = __INVARIANT__ ? require('js-schema') : null;

/**
 * Like `Object` but all properties frozen (recursively) using {@link module:utils/freeze.deepFreeze}
 * @typedef {Object} FrozenObject
 */

/**
 * Either an `Object` or `FrozenObject`, which cannot be mutated in order to modify client/store state.
 * @typedef {Object|FrozenObject} SafeReference
 */

/**
 * @typedef {string|Number} StringOrNumber
 */

/**
 * @typedef {number} BasisPoints
 */

/**
 * Schema for BasisPoints
 * @private
 */
var BasisPoints = schema(Number.min(0).below(10001));

/**
 * Map experimentId to preferred variationId for all experiments in a layer
 * @typedef {Object.<string>} LayerPreferredVariationMap
 */

/**
 * schema
 * @private
 */
var LayerPreferredVariationMap = schema({
  '*': String,
});

/**
 * [Input to Client Decision Engine](https://github.com/optimizely/client-decision-engine/blob/master/README.md#DecisionTicket)
 *
 * @typedef {Object} DecisionTicket
 * @property {string[]} audienceIds
 * @property {string} [bucketingId]
 * @property {BasisPoints} [globalHoldback]
 * @property {LayerPreferredVariationMap} [preferredVariationMap]
 * @property {string} [pageId]  Undefined when decision policy does not direct the decision engine
 * to include the pageId in the decisionTicket.
 * @property {string} [activationId] To allow deterministic bucketing for each client activation
 */

/**
 * schema
 * @extends GroupDecisionTicket
 * @private
 */
var DecisionTicket = schema({
  audienceIds: Array.of(String),
  bucketingId: [String, null],
  globalHoldback: [null, BasisPoints],
  preferredVariationMap: [null, LayerPreferredVariationMap],
  pageId: [null, String],
  activationId: [String, null],
});

/**
 * [Output from Client Decision Engine](https://github.com/optimizely/client-decision-engine/blob/master/README.md#Decision)
 * The result of attempting to decide a Layer.
 *
 * @typedef {Object} Decision
 * @property {string} layerId
 * @property {string} [experimentId]
 * @property {string} [variationId]
 * @property {Boolean} isLayerHoldback
 * @property {string} [reason]
 * @property {Error} [error]
 */

/**
 * schema
 * @private
 */
var Decision = schema({
  layerId: String,
  experimentId: [null, String],
  variationId: [null, String],
  isLayerHoldback: Boolean,
  isLayerConcluded: [null, Boolean],
  reason: [null, String],
  error: [null, Error],
});

/**
 * The result of attempting to decide a Group.
 *
 * @typedef {Object} GroupDecision
 * @property {string} [layerId]
 * @property {string} [reason]
 */
var GroupDecision = schema({
  layerId: [null, String],
  reason: [null, String],
});

/**
 * Contains all the information Decision.decideGroup needs to
 * try to produce a GroupDecision.
 *
 * @private
 */
var GroupDecisionTicket = schema({
  bucketingId: String,
  preferredLayerId: [null, String],
});

/**
 * @typedef {Object} CampaignDecision
 * @property {string} campaignId
 * @property {string} [experimentId]
 * @property {string} [variationId]
 * @property {Boolean} isCampaignHoldback
 * @property {string} [reason]
 * @property {Error} [error]
 */

/**
 * schema
 * @private
 */
var CampaignDecision = schema({
  campaignId: String,
  experimentId: [null, String],
  variationId: [null, String],
  isCampaignHoldback: Boolean,
  reason: [null, String],
  error: [null, Error],
});

/**
 * Map layerId to LayerPreferredVariationMap, for all layers
 * @typedef {Object.<LayerPreferredVariationMap>} PreferredVariationMap
 */

/**
 * @typedef {Object} ViewState
 * @property {string} id
 * @property {Boolean} isActive
 * @property {number} [activatedTimestamp]
 * @property {Object} metadata
 * @property {Object} parsedMetadata
 * @property {Object} userSuppliedMetadata
 */

/**
 * schema
 * @private
 */
var ViewState = schema({
  id: String,
  isActive: [null, Boolean],
  activatedTimestamp: [Number, null],
  activationEventId: [null, String],
  metadata: Object,
  parsedMetadata: Object,
  userSuppliedMetadata: Object,
});

/**
 * @typedef {Object} LayerState
 * @property {string} layerId
 * @property {string} revision
 * @property {string} [projectId]
 * @property {string} [namespace] The namespace in which we're allowed to persist this LayerState (undefined for
 *                                     legacy layer states).
 * @property {string} [pageId] Undefined when decision policy does not direct decision engine
 * to include the pageId in the decisionTicket
 * @property {string} [viewId] Backwards-compatibility alias of pageId
 * @property {DecisionTicket} decisionTicket
 * @property {Decision} decision
 * @property {number} decisionTimestamp The timestamp when the decision was made
 *   (for the purposes of reconciling which of two decisions is newer in conflict scenarios)
 * @property {string} [decisionActivationId] The activationId of the client activation in which the decision was made
 *   (used to determine the "isActive" property in state API)
 * @property {string} [decisionEventid] ID of the corresponding decision event
 *   (for the purposes of joining with additional data not in the layerState)
 */

/**
 * schema
 * @private
 */
var LayerState = schema({
  layerId: String,
  revision: [String, null],
  projectId: [String, null],
  pageId: [String, null],
  namespace: [String, null],
  viewId: [String, null],
  decisionTicket: [DecisionTicket, null],
  decision: [Decision, null],
  decisionActivationId: [String, null],
  decisionTimestamp: [Number, null],
  decisionEventId: [String, null],
});

/**
 * Compact representation of layerState for serialization to localStorage. Field references refer to the corresponding fields in {@see LayerState
 * @typedef {Object} PersistableLayerState
 *
 * @property {string} i layerId
 * @property {string} [p] pageId
 * @property {string} [x] decision.experimentId
 * @property {string} [v] decision.variationId
 * @property {Boolean} [h] decision.isLayerHoldback
 * @property {Array.<String>} [a] decisionTicket.audienceIds
 * @property {number} [t] decisionTimestamp
 */

/**
 * schema
 * @private
 */
var PersistableLayerState = schema({
  i: String,
  p: [String, null],
  x: [String, null],
  v: [String, null],
  h: [Boolean, null],
  a: [Array.of(String), null],
  t: [Number, null],
});

/**
 * @typedef {Object} LayerStateAnalytics
 * @property {string} layerId
 * @property {Decision} decision
 * @property {string} decisionEventid ID of the corresponding decision event
 *   (for the purposes of joining with additional data not in the layerState)
 */

/**
 * schema
 * @private
 */
var LayerStateAnalytics = schema({
  layerId: String,
  decision: [Decision, null],
  decisionEventId: [String, null],
});

/**
 * Activation-specific context that must be relayed along with individual event data
 * if tracking events from an unrelated client activation.
 *
 * For more details, see the CJS-3302 design doc which introduced the concept of a PersistableEventBatch:
 *    https://confluence.sso.episerver.net/display/EXPENG/Design+Doc%3A+persisting+events+from+the+Edge+microsnippet
 * and the CJS-1089 design doc which had previously introduced the concept of a PersistableDecisionWithContext:
 *    https://confluence.sso.episerver.net/display/EXPENG/Design+Doc%3A+improved+redirect+tracking
 *
 * @typedef {Object} PersistableContextFields
 * @property {string} accountId
 * @property {string} [activationId] Only read by snippets older than ~0.134.0
 *                                      TODO(CJS-3580): Resave snippets and then stop relaying this.
 * @property {string} [clientName] May be missing in data send by snippets older than 0.127.0
 *                                    (which is necessarily a PersistableDecisionWithContext in a redirect cookie).
 *                                    TODO(CJS-3580): Resave snippets and then require that this be relayed.
 * @property {string} clientVersion
 * @property {string} namespace The namespace in which we're allowed to persist the LayerState for this decision.
 * @property {string} projectId
 * @property {string} referrer Only needed for decision events.
 *                             Unlike GlobalStore.getEffectiveReferrer(),
 *                             this is defined even on page loads that are not redirect targets.
 *                             Compare with document.referrer in order to determine whether this is meaningful enough
 *                             to register with third-party analytics. (We should register the referrer with third-party
 *                             analytics as rarely as possible, so that customers' first-party and third-party code can
 *                             register their own referrer when applicable.)
 * @property {string} revision Only needed for decision events.
 * @property {string} [sessionId] Only read by snippets older than ~0.134.0
 *                                   TODO(CJS-3580): Resave snippets and then stop relaying this.
 * @property {number} timestamp May be overridden by individual click and custom events.
 * @property {string} visitorId
 */

/**
 * @private
 */
var PersistableContextFields = {
  accountId: String,
  activationId: [String, null],
  clientName: [String, null],
  clientVersion: String,
  // TODO: possibly deprecated, see CJS-2596
  namespace: String,
  projectId: String,
  referrer: String,
  revision: String,
  sessionId: [String, null],
  timestamp: Number,
  visitorId: String,
};

/**
 * Data about a specific decision that needs to be tracked.
 *
 * If relayed and tracked from another client activation, context from the original client activation must be relayed
 * too (see PersistableContextFields). Context from the tracking client must also be included, producing a
 * DecisionData which is sent to various tracker plugins).
 *
 * @typedef {Object} PersistableDecisionFields
 * @property {string} decisionId
 * @property {string[]} [decisionTicketAudienceIds] Only read by snippets older than ~0.134.0
 *                                                     TODO(CJS-3580): Resave snippets and then stop relaying this.
 * @property {string} [experimentId] (Presumably a string if we find ourselves performing a redirect.)
 * @property {string} [experimentName]
 * @property {Object} [integrationSettings] Map of plugin name to applicable settings
 * @property {number} [integrationStringVersion=1] May be missing in data sent by snippets older than ~0.134.0
 *                                                      (which is necessarily a PersistableDecisionWithContext
 *                                                      in a redirect cookie).
 *                                                      TODO(CJS-3580): Resave snippets and then require that this be relayed.
 * @property {string} layerId
 * @property {string} [layerName]
 * @property {string} layerPolicy
 * @property {string} [pageId] Will be a string iff this is an "offer consistency" layer.
 * @property {string} [variationId] (Presumably a string if we find ourselves performing a redirect.)
 * @property {string} [variationName]
 */

/**
 * @private
 */
var PersistableDecisionFields = {
  decisionId: String,
  decisionTicketAudienceIds: [Array.of(String), null],
  experimentId: [String, null],
  experimentName: [String, null],
  integrationSettings: [Object, null],
  integrationStringVersion: [Number, null],
  layerId: String,
  layerName: [String, null],
  layerPolicy: [String],
  pageId: [String, null],
  variationId: [String, null],
  variationName: [String, null],
};

/**
 * Stringified and URI-encoded version of PersistableDecisionWithContext, suitable for relaying via cookie, fragment, or
 * query param.
 *
 * May not include visitorId if we have reason to believe that it's being relayed via the
 * optimizelyEndUserId cookie.
 *
 * Example:
 *   's=1-2-3-4&as=Audience%20ID,Audience%20ID%2C%20with%20comma&d=Decision%20ID&aId=Activation%20ID&v=777&r=https%3A%2F%2Fwww.optimizely.com%2F&t=1234567890&p=2&n=42&cV=1.0.0&a=1&l=700&lN=Layer%20700&lP=equal_priority&x=770&xN=Experiment%20770&vN=Variation%20777'
 *
 * For details on how this is translated to and from PersistableDecisionWithContext, see analytics/relay.js.
 *
 * @typedef {string} StringsifiedDecisionWithContext
 */

/**
 * @typedef {Object} PersistableViewEventFields
 * @property {string} eventId
 * @property {Object} eventTags
 * @property {string} pageId
 * @property {string} pageApiName
 */

/**
 * @private
 */
var PersistableViewEventFields = {
  eventId: String,
  eventTags: Object,
  pageId: String,
  pageApiName: String,
};

/**
 * @typedef {Object} PersistableClickEventFields
 * @property {string} eventApiName
 * @property {string} eventCategory
 * @property {string} eventEntityId
 * @property {string} eventId
 * @property {Object} eventTags
 * @property {number} timestamp Overrides the timestamp in the PersistableContextFields
 *                              since this click event may have happened after (and not concurrently with)
 *                              the client activation in the source context.
 */

/**
 * @private
 */
var PersistableClickEventFields = {
  eventApiName: String,
  eventCategory: String,
  eventEntityId: String,
  eventId: String,
  eventTags: Object,
  timestamp: Number,
};

/**
 * @typedef {Object} PersistableCustomEventFields
 * @property {string} eventApiName
 * @property {string} eventCategory
 * @property {string} [eventEntityId] TODO(CJS-3555) make sure it's actually null, not undefined
 * @property {string} eventId
 * @property {Object} eventTags
 * @property {number} timestamp Overrides the timestamp in the PersistableContextFields
 *                              since this custom event may have happened after (and not concurrently with)
 *                              the client activation in the source context.
 */

/**
 * @private
 */
var PersistableCustomEventFields = {
  eventApiName: String,
  eventCategory: String,
  eventEntityId: [String, null],
  eventId: String,
  eventTags: Object,
  timestamp: Number,
};

/**
 * Data that must be relayed from the pre-redirect page if decision tracking occurs on a page load other than the one
 * on which the decision was made.
 *
 * For more details, see the CJS-1089 design doc:
 *    https://confluence.sso.episerver.net/display/EXPENG/Design+Doc%3A+improved+redirect+tracking
 *
 * @typedef {Object} PersistableDecisionWithContext
 *
 * Inherited from PersistableContextFields
 * @property {string} accountId
 * @property {string} [activationId] Only read by snippets older than ~0.134.0
 *                                      TODO(CJS-3580): Resave snippets and then stop relaying this.
 * @property {string} [clientName] May be missing in data send by snippets older than 0.127.0
 *                                    (which is necessarily a PersistableDecisionWithContext in a redirect cookie).
 *                                    TODO(CJS-3580): Resave snippets and then require that this be relayed.
 * @property {string} clientVersion
 * @property {string} namespace The namespace in which we're allowed to persist the LayerState for this decision.
 * @property {string} projectId
 * @property {string} referrer Only needed for decision events.
 *                             Unlike GlobalStore.getEffectiveReferrer(),
 *                             this is defined even on page loads that are not redirect targets.
 *                             Compare with document.referrer in order to determine whether this is meaningful enough
 *                             to register with third-party analytics. (We should register the referrer with third-party
 *                             analytics as rarely as possible, so that customers' first-party and third-party code can
 *                             register their own referrer when applicable.)
 * @property {string} revision Only needed for decision events.
 * @property {string} [sessionId] Only read by snippets older than ~0.134.0
 *                                   TODO(CJS-3580): Resave snippets and then stop relaying this.
 * @property {number} timestamp May be overridden by individual click and custom events.
 * @property {string} visitorId
 *
 * Inherited from PersistableDecisionFields
 * @property {string} decisionId
 * @property {string[]} [decisionTicketAudienceIds] Only read by snippets older than ~0.134.0
 *                                                     TODO(CJS-3580): Resave snippets and then stop relaying this.
 * @property {string} [experimentId] (Presumably a string if we find ourselves performing a redirect.)
 * @property {string} [experimentName]
 * @property {Object} [integrationSettings] Map of plugin name to applicable settings
 * @property {number} [integrationStringVersion=1] May be missing in data sent by snippets older than ~0.134.0
 *                                                      (which is necessarily a PersistableDecisionWithContext
 *                                                      in a redirect cookie).
 *                                                      TODO(CJS-3580): Resave snippets and then require that this be relayed.
 * @property {string} layerId
 * @property {string} [layerName]
 * @property {string} layerPolicy
 * @property {string} [pageId] Will be a string iff this is an "offer consistency" layer.
 * @property {string} [variationId] (Presumably a string if we find ourselves performing a redirect.)
 * @property {string} [variationName]
 */

/**
 * schema
 */
var PersistableDecisionWithContext = schema(_.extend({}, PersistableContextFields, PersistableDecisionFields));

/**
 * Optimizely Edge event data relayed from an Edge microsnippet. Safe to track on any page load.
 *
 * This is only suitable for relaying as JSON in localStorage. It's too big and obtuse to URL-encode.
 *
 * @typedef {Object} PersistableEventBatch
 * @property {PersistableContextFields} context
 * @property {PersistableDecisionFields[]} [decisions]
 * @property {PersistableViewEventFields[]} [pageviews]
 * @property {PersistableClickEventFields[]} [clicks]
 * @property {PersistableCustomEventFields[]} [custom]
 */

/**
 * schema
 */
var PersistableEventBatch = schema({
  context: schema(PersistableContextFields),
  decisions: [null, Array.of(schema(PersistableDecisionFields))],
  pageviews: [null, Array.of(schema(PersistableViewEventFields))],
  clicks: [null, Array.of(schema(PersistableClickEventFields))],
  custom: [null, Array.of(schema(PersistableCustomEventFields))],
});

/**
 * Context that is relevant for (all) events tracked from the current client activation. I.e. the "tracking" context.
 *
 * This is used to enrich relayed data (PersistableContextFields, etc.).
 *
 * @typedef {Object} TrackingContextFields
 * @property {string} activationId
 *   Only referenced by tracker_avro which is no longer used in any customer's snippets.
 * @property {ViewState[]} activeViewStates
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, but this is only referenced by tracker_avro which is no longer used in any customer's snippets.
 * @property {string} [anonymizeIP]
 * @property {string} clientName Filled in with ClientMetadata.ENGINE if missing from PersistableContextFields
 *                               TODO(CJS-3580): Resave snippets so we don't need to fill this in.
 * @property {Boolean} isGlobalHoldback
 * @property {LayerStateAnalytics[]} layerStates
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, but it's probably okay because
 *   - Tracking of such *relayed* data should always be done with enrich_decisions: true,
 *     so events will automatically be attributed to any previously-tracked decisions.
 *   - If decisions and conversions are concurrent (i.e. would have been tracked in the same v1 event batch)
 *     then they could and should have been relayed in the same PersistableEventBatch (which would result in them being
 *     tracked in the same batch)
 *   - There *could* be an issue if a decision and conversion are tracked separately and decision tracking is delayed
 *     longer than conversion tracking. The decision _should_ be backdated but it's not clear whether that'll really
 *     work if the conversion was tracked during a prior session.
 * @property {string} sessionId
 *   No longer used in any customer's snippets thanks to __EXPERIMENTAL_AUTO_SESSION_ID__.
 * @property {FeatureValue[]} userFeatures
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, especially if the relaying context was on a different origin or was using a different snippet/manifest.
 * @private
 */

/**
 * schema
 * @private
 */
var TrackingContextFields = {
  activationId: String,
  activeViewStates: Array.of(ViewState),
  anonymizeIP: [Boolean, null],
  clientName: String,
  isGlobalHoldback: Boolean,
  layerStates: Array.of(LayerStateAnalytics),
  sessionId: String,
  userFeatures: Array.of(FeatureValue),
};

/**
 * @typedef {Object} ContextFields
 * @property {string} accountId
 * @property {string} activationId
 *   Only referenced by tracker_avro which is no longer used in any customer's snippets.
 * @property {ViewState[]} activeViewStates
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, but this is only referenced by tracker_avro which is no longer used in any customer's snippets.
 * @property {string} [anonymizeIP]
 * @property {string} clientName Filled in with ClientMetadata.ENGINE if missing from PersistableContextFields
 *                               TODO(CJS-3580): Resave snippets so we don't need to fill this in.
 * @property {string} clientVersion
 * @property {Boolean} isGlobalHoldback
 * @property {LayerStateAnalytics[]} layerStates
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, but it's probably okay because
 *   - Tracking of such *relayed* data should always be done with enrich_decisions: true,
 *     so events will automatically be attributed to any previously-tracked decisions.
 *   - If decisions and conversions are concurrent (i.e. would have been tracked in the same v1 event batch)
 *     then they could and should have been relayed in the same PersistableEventBatch (which would result in them being
 *     tracked in the same batch)
 *   - There *could* be an issue if a decision and conversion are tracked separately and decision tracking is delayed
 *     longer than conversion tracking. The decision _should_ be backdated but it's not clear whether that'll really
 *     work if the conversion was tracked during a prior session.
 * @property {string} namespace The namespace in which we're allowed to persist the LayerState for this decision.
 * @property {string} projectId
 * @property {string} referrer Only needed for decision events.
 *                             Unlike GlobalStore.getEffectiveReferrer(),
 *                             this is defined even on page loads that are not redirect targets.
 *                             Compare with document.referrer in order to determine whether this is meaningful enough
 *                             to register with third-party analytics. (We should register the referrer with third-party
 *                             analytics as rarely as possible, so that customers' first-party and third-party code can
 *                             register their own referrer when applicable.)
 * @property {string} revision Only needed for decision events.
 * @property {string} sessionId
 *   No longer used in any customer's snippets thanks to __EXPERIMENTAL_AUTO_SESSION_ID__.
 * @property {number} timestamp May be overridden by individual click and custom events.
 * @property {FeatureValue[]} userFeatures
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, especially if the relaying context was on a different origin or was using a different snippet/manifest.
 * @property {string} visitorId
 * @private
 */

/**
 * schema
 * @private
 */
var ContextFields = _.extend({}, PersistableContextFields, TrackingContextFields);

/**
 * Data that's passed to tracker plugins' trackLayerDecision callbacks.
 *
 * The quantity and timing of trackLayerDecision calls is determined by the plugin's preRedirectPolicy,
 * postRedirectPolicy, and nonRedirectPolicy.
 *
 * When tracking is specified by the postRedirectPolicy, the plugin will be called on post-redirect pages. Some of
 * the data that it receives (the PersistableContextFields and PersistableDecisionFields subset) will have been relayed
 * from the pre-redirect page, and the will have been computed on the post-redirect page.
 *
 * TODO(CJS-3302): Maybe introduce Edge-related policies for events from earlier on the page load? Or does nonRedirectPolicy suffice?
 *                 Also, note that Edge-captured redirect decisions (see CJS-3323) look like ordinary Edge-relayed
 *                 decisions, so the postRedirectPolicy would not apply unless the redirect cookie is also still set.
 *
 * For more details, see the CJS-1089 design doc:
 *    https://confluence.sso.episerver.net/display/EXPENG/Design+Doc%3A+improved+redirect+tracking
 *
 * @typedef {Object} DecisionData
 *
 * Inherited from the context
 * @property {string} accountId
 * @property {string} activationId
 *   Only referenced by tracker_avro which is no longer used in any customer's snippets.
 * @property {ViewState[]} activeViewStates
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, but this is only referenced by tracker_avro which is no longer used in any customer's snippets.
 * @property {string} [anonymizeIP]
 * @property {string} clientName Filled in with ClientMetadata.ENGINE if missing from PersistableContextFields
 *                               TODO(CJS-3580): Resave snippets so we don't need to fill this in.
 * @property {string} clientVersion
 * @property {Boolean} isGlobalHoldback
 * @property {LayerStateAnalytics[]} layerStates
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, but it's probably okay because
 *   - Tracking of such *relayed* data should always be done with enrich_decisions: true,
 *     so events will automatically be attributed to any previously-tracked decisions.
 *   - If decisions and conversions are concurrent (i.e. would have been tracked in the same v1 event batch)
 *     then they could and should have been relayed in the same PersistableEventBatch (which would result in them being
 *     tracked in the same batch)
 *   - There *could* be an issue if a decision and conversion are tracked separately and decision tracking is delayed
 *     longer than conversion tracking. The decision _should_ be backdated but it's not clear whether that'll really
 *     work if the conversion was tracked during a prior session.
 * @property {string} namespace The namespace in which we're allowed to persist the LayerState for this decision.
 * @property {string} projectId
 * @property {string} referrer Only needed for decision events.
 *                             Unlike GlobalStore.getEffectiveReferrer(),
 *                             this is defined even on page loads that are not redirect targets.
 *                             Compare with document.referrer in order to determine whether this is meaningful enough
 *                             to register with third-party analytics. (We should register the referrer with third-party
 *                             analytics as rarely as possible, so that customers' first-party and third-party code can
 *                             register their own referrer when applicable.)
 * @property {string} revision Only needed for decision events.
 * @property {string} sessionId
 *   No longer used in any customer's snippets thanks to __EXPERIMENTAL_AUTO_SESSION_ID__.
 * @property {number} timestamp May be overridden by individual click and custom events.
 * @property {FeatureValue[]} userFeatures
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, especially if the relaying context was on a different origin or was using a different snippet/manifest.
 * @property {string} visitorId
 *
 * Unique to this decision
 * @property {string} decisionId
 * @property {string[]} decisionTicketAudienceIds
 *   Only referenced by tracker_avro which is no longer used in any customer's snippets.
 * @property {string} [experimentId] (Presumably a string if we find ourselves performing a redirect.)
 * @property {string} [experimentName]
 * @property {Object} [integrationSettings] Map of plugin name to applicable settings
 * @property {number} integrationStringVersion
 * @property {Boolean} isLayerHoldback
 * @property {string} layerId
 * @property {string} [layerName]
 * @property {string} layerPolicy
 * @property {string} [pageId] Will be a string iff this is an "offer consistency" layer.
 * @property {string} [timing] Indicates the timing of a given call to a tracker plugin.
 *                                     A given decision will be passed to trackers multiple times.
 * @property {string} [variationId] (Presumably a string if we find ourselves performing a redirect.)
 * @property {string} [variationName]
 */

/**
 * schema
 */
var DecisionData = schema(_.extend({}, ContextFields, PersistableDecisionFields, {
  decisionTicketAudienceIds: Array.of(String),
  timing: [null, String],
  integrationStringVersion: Number,
  isLayerHoldback: Boolean,
  isLayerConcluded: [null, Boolean],
}));

/**
 * Standard data structure in client for all viewActivated events, a.k.a. pageview events.
 *
 * @typedef {Object} ViewEventData
 *
 * Inherited from the context
 * @property {string} accountId
 * @property {string} activationId
 *   Only referenced by tracker_avro which is no longer used in any customer's snippets.
 * @property {ViewState[]} activeViewStates
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, but this is only referenced by tracker_avro which is no longer used in any customer's snippets.
 * @property {string} [anonymizeIP]
 * @property {string} clientName Filled in with ClientMetadata.ENGINE if missing from PersistableContextFields
 *                               TODO(CJS-3580): Resave snippets so we don't need to fill this in.
 * @property {string} clientVersion
 * @property {Boolean} isGlobalHoldback
 * @property {LayerStateAnalytics[]} layerStates
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, but it's probably okay because
 *   - Tracking of such *relayed* data should always be done with enrich_decisions: true,
 *     so events will automatically be attributed to any previously-tracked decisions.
 *   - If decisions and conversions are concurrent (i.e. would have been tracked in the same v1 event batch)
 *     then they could and should have been relayed in the same PersistableEventBatch (which would result in them being
 *     tracked in the same batch)
 *   - There *could* be an issue if a decision and conversion are tracked separately and decision tracking is delayed
 *     longer than conversion tracking. The decision _should_ be backdated but it's not clear whether that'll really
 *     work if the conversion was tracked during a prior session.
 * @property {string} namespace The namespace in which we're allowed to persist the LayerState for this decision.
 * @property {string} projectId
 * @property {string} referrer Only needed for decision events.
 *                             Unlike GlobalStore.getEffectiveReferrer(),
 *                             this is defined even on page loads that are not redirect targets.
 *                             Compare with document.referrer in order to determine whether this is meaningful enough
 *                             to register with third-party analytics. (We should register the referrer with third-party
 *                             analytics as rarely as possible, so that customers' first-party and third-party code can
 *                             register their own referrer when applicable.)
 * @property {string} revision Only needed for decision events.
 * @property {string} sessionId
 *   No longer used in any customer's snippets thanks to __EXPERIMENTAL_AUTO_SESSION_ID__.
 * @property {number} timestamp May be overridden by individual click and custom events.
 * @property {FeatureValue[]} userFeatures
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, especially if the relaying context was on a different origin or was using a different snippet/manifest.
 * @property {string} visitorId
 *
 * Unique to this pageview event
 * @property {string} eventId
 * @property {Object} eventTags
 * @property {string} pageId
 * @property {string} pageApiName
 * @property {string} viewCategory
 */

/**
 * schema
 */
var ViewEventData = schema(_.extend({}, ContextFields, PersistableViewEventFields, {
  // Technically this _should_ be pre-computed and relayed as part of PersistableViewEventFields,
  // but it's only referenced by tracker_avro which is no longer used in any customer's snippets.
  viewCategory: String,
}));

/**
 * Standard data structure in client for all viewActivated events
 *
 * @typedef {Object} ClickEventData
 *
 * Inherited from the context
 * @property {string} accountId
 * @property {string} activationId
 *   Only referenced by tracker_avro which is no longer used in any customer's snippets.
 * @property {ViewState[]} activeViewStates
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, but this is only referenced by tracker_avro which is no longer used in any customer's snippets.
 * @property {string} [anonymizeIP]
 * @property {string} clientName Filled in with ClientMetadata.ENGINE if missing from PersistableContextFields
 *                               TODO(CJS-3580): Resave snippets so we don't need to fill this in.
 * @property {string} clientVersion
 * @property {Boolean} isGlobalHoldback
 * @property {LayerStateAnalytics[]} layerStates
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, but it's probably okay because
 *   - Tracking of such *relayed* data should always be done with enrich_decisions: true,
 *     so events will automatically be attributed to any previously-tracked decisions.
 *   - If decisions and conversions are concurrent (i.e. would have been tracked in the same v1 event batch)
 *     then they could and should have been relayed in the same PersistableEventBatch (which would result in them being
 *     tracked in the same batch)
 *   - There *could* be an issue if a decision and conversion are tracked separately and decision tracking is delayed
 *     longer than conversion tracking. The decision _should_ be backdated but it's not clear whether that'll really
 *     work if the conversion was tracked during a prior session.
 * @property {string} namespace The namespace in which we're allowed to persist the LayerState for this decision.
 * @property {string} projectId
 * @property {string} referrer Only needed for decision events.
 *                             Unlike GlobalStore.getEffectiveReferrer(),
 *                             this is defined even on page loads that are not redirect targets.
 *                             Compare with document.referrer in order to determine whether this is meaningful enough
 *                             to register with third-party analytics. (We should register the referrer with third-party
 *                             analytics as rarely as possible, so that customers' first-party and third-party code can
 *                             register their own referrer when applicable.)
 * @property {string} revision Only needed for decision events.
 * @property {string} sessionId
 *   No longer used in any customer's snippets thanks to __EXPERIMENTAL_AUTO_SESSION_ID__.
 * @property {FeatureValue[]} userFeatures
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, especially if the relaying context was on a different origin or was using a different snippet/manifest.
 * @property {string} visitorId
 *
 * Unique to this click event
 * @property {string} eventApiName
 * @property {string} eventCategory
 * @property {string} eventEntityId
 * @property {string} eventId
 * @property {Object} eventTags
 * @property {string} pageId
 * @property {string} selector
 * @property {number} timestamp Comes from PersistableClickEventFields, not PersistableContextFields,
 *                              since this click event may have happened after (and not concurrently with)
 *                              the client activation in the source context.
 */

/**
 * schema
 */
var ClickEventData = schema(_.extend({}, ContextFields, PersistableClickEventFields, {
  // Technically these _should_ be pre-computed and relayed as part of PersistableViewEventFields,
  // but they're only referenced by tracker_avro which is no longer used in any customer's snippets.
  pageId: String,
  selector: String,
}));

/**
 * Data passed from the Analytics module for `optimizely.trackEvent`
 *
 * @typedef {Object} CustomEventData
 *
 * Inherited from the context
 * @property {string} accountId
 * @property {string} activationId
 *   Only referenced by tracker_avro which is no longer used in any customer's snippets.
 * @property {ViewState[]} activeViewStates
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, but this is only referenced by tracker_avro which is no longer used in any customer's snippets.
 * @property {string} [anonymizeIP]
 * @property {string} clientName Filled in with ClientMetadata.ENGINE if missing from PersistableContextFields
 *                               TODO(CJS-3580): Resave snippets so we don't need to fill this in.
 * @property {string} clientVersion
 * @property {Boolean} isGlobalHoldback
 * @property {LayerStateAnalytics[]} layerStates
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, but it's probably okay because
 *   - Tracking of such *relayed* data should always be done with enrich_decisions: true,
 *     so events will automatically be attributed to any previously-tracked decisions.
 *   - If decisions and conversions are concurrent (i.e. would have been tracked in the same v1 event batch)
 *     then they could and should have been relayed in the same PersistableEventBatch (which would result in them being
 *     tracked in the same batch)
 *   - There *could* be an issue if a decision and conversion are tracked separately and decision tracking is delayed
 *     longer than conversion tracking. The decision _should_ be backdated but it's not clear whether that'll really
 *     work if the conversion was tracked during a prior session.
 * @property {string} namespace The namespace in which we're allowed to persist the LayerState for this decision.
 * @property {string} projectId
 * @property {string} referrer Only needed for decision events.
 *                             Unlike GlobalStore.getEffectiveReferrer(),
 *                             this is defined even on page loads that are not redirect targets.
 *                             Compare with document.referrer in order to determine whether this is meaningful enough
 *                             to register with third-party analytics. (We should register the referrer with third-party
 *                             analytics as rarely as possible, so that customers' first-party and third-party code can
 *                             register their own referrer when applicable.)
 * @property {string} revision Only needed for decision events.
 * @property {string} sessionId
 *   No longer used in any customer's snippets thanks to __EXPERIMENTAL_AUTO_SESSION_ID__.
 * @property {FeatureValue[]} userFeatures
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, especially if the relaying context was on a different origin or was using a different snippet/manifest.
 * @property {string} visitorId
 *
 * Unique to this custom event
 * @property {string} eventApiName
 * @property {string} eventCategory
 * @property {string} [eventEntityId] TODO(CJS-3555) make sure it's actually null, not undefined
 * @property {string} eventId
 * @property {Object} eventTags
 * @property {number} timestamp Comes from PersistableCustomEventFields, not PersistableContextFields,
 *                              since this custom event may have happened after (and not concurrently with)
 *                              the client activation in the source context.
 */

/**
 * schema
 */
var CustomEventData = schema(_.extend({}, ContextFields, PersistableCustomEventFields));

/**
 * Data passed to analytics trackers for client activation event hooks
 *
 * @typedef {Object} ClientActivationEventData
 *
 * Inherited from the context
 * @property {string} accountId
 * @property {string} activationId
 *   Only referenced by tracker_avro which is no longer used in any customer's snippets.
 * @property {ViewState[]} activeViewStates
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, but this is only referenced by tracker_avro which is no longer used in any customer's snippets.
 * @property {string} [anonymizeIP]
 * @property {string} clientName Filled in with ClientMetadata.ENGINE if missing from PersistableContextFields
 *                               TODO(CJS-3580): Resave snippets so we don't need to fill this in.
 * @property {string} clientVersion
 * @property {Boolean} isGlobalHoldback
 * @property {LayerStateAnalytics[]} layerStates
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, but it's probably okay because
 *   - Tracking of such *relayed* data should always be done with enrich_decisions: true,
 *     so events will automatically be attributed to any previously-tracked decisions.
 *   - If decisions and conversions are concurrent (i.e. would have been tracked in the same v1 event batch)
 *     then they could and should have been relayed in the same PersistableEventBatch (which would result in them being
 *     tracked in the same batch)
 *   - There *could* be an issue if a decision and conversion are tracked separately and decision tracking is delayed
 *     longer than conversion tracking. The decision _should_ be backdated but it's not clear whether that'll really
 *     work if the conversion was tracked during a prior session.
 * @property {string} namespace The namespace in which we're allowed to persist the LayerState for this decision.
 * @property {string} projectId
 * @property {string} referrer Only needed for decision events.
 *                             Unlike GlobalStore.getEffectiveReferrer(),
 *                             this is defined even on page loads that are not redirect targets.
 *                             Compare with document.referrer in order to determine whether this is meaningful enough
 *                             to register with third-party analytics. (We should register the referrer with third-party
 *                             analytics as rarely as possible, so that customers' first-party and third-party code can
 *                             register their own referrer when applicable.)
 * @property {string} revision Only needed for decision events.
 * @property {string} sessionId
 *   No longer used in any customer's snippets thanks to __EXPERIMENTAL_AUTO_SESSION_ID__.
 * @property {number} timestamp May be overridden by individual click and custom events.
 * @property {FeatureValue[]} userFeatures
 *   Potentially incomplete or incorrect if the tracking context is different from the context that produced the relayed
 *   events, especially if the relaying context was on a different origin or was using a different snippet/manifest.
 * @property {string} visitorId
 *
 * Unique to this client activation event
 * @property {Object} eventTags
 */

/**
 * schema
 */
var ClientActivationEventData = schema(_.extend({}, ContextFields, {
  eventTags: Object,
}));

/**
 *
 * @typedef SendEventsApiEvent
 * @property {string} type
 * @property {string} name
 */
var SendEventsApiEvent = schema({
  type: 'analytics',
  name: 'sendEvents'
});

/**
 * @typedef {number} LogLevel
 */

/**
 * schema
 * @private
 */
var LogLevel = schema(_.toArray(enums.LogLevel));

/**
 * @typedef {string} Negator
 */

/**
 * @private
 */
var Negator = schema(['not']);

/**
 * @typedef {string} Comparator
 */

/**
 * @private
 */
var Comparator = schema(['eq', 'is', 'lt', 'gt', 'lte', 'gte', 'in', 'between', 'contains', 'regex', 'and', 'or', 'exists']);

/**
 * @typedef {string} ArithmeticOperator
 */

/**
 * @private
 */
var ArithmeticOperator = schema(['+', '-', '*', '/', '%']);

/**
 * @typedef {string} AggregateOperator
 */

/**
 * @private
 */
var AggregateOperator = schema(['sum', 'count', 'max', 'min', 'avg']);

/**
 * Literals can be a number, boolean, or a string.
 * @typedef {Number|string|Boolean} Literal
 */

/**
 * schema
 * @private
 */
var Literal = schema([Number, String, Boolean]);

/**
 * Field specification.
 * @typedef {Object} FieldSpec
 * @property {Literal[]} field
 */
var FieldSpec = schema({
  field: Array.of(Literal),
});

/**
 * Functions to evaluate.
 * @typedef {Object} EvalSpec
 * @property {string[]} eval
 */
var EvalSpec = schema({
  eval: ['now'],
});

/**
 * Value specification.
 * @typedef {Object} ValueSpec
 * @property {Literal|Literal[]} value
 */
var ValueSpec = schema({
  value: [Literal, Array.of(Literal)],
});

/**
 * @typedef {FieldSpec|EvalSpec|ValueSpec} LeafOperand
 */

/**
 * Operands at the leaf of the expression tree.
 */
var LeafOperand = schema([FieldSpec, EvalSpec, ValueSpec]);

/**
 * A recursive operand.
 * @typedef {Object} RecursiveOperand
 * @property {Negator|Comparator|ArithmeticOperator|AggregateOperator} op
 * @property {Operand[]} args
 */
var RecursiveOperand = schema({
  op: [ Negator, Comparator, ArithmeticOperator, AggregateOperator ],
  args: Array.of([LeafOperand, schema.self]),  // i.e. Array.of(Operand)
});

/**
 * @typedef {LeafOperand|RecursiveOperand} Operand
 */

/**
 * An operand can be a leaf operand or a recursive operand.
 */
var Operand = schema([LeafOperand, RecursiveOperand]);

/**
 * Aggregate field.
 * @typedef {Object} AggregateField
 * @property {AggregateOperator} op
 * @property {FieldSpec[]} args
 */
var AggregateField = schema({
  op: AggregateOperator,
  args: Array.of(1, FieldSpec),
});

/**
 * Order field.
 * @typedef {Object} OrderField
 * @property {Literal[]} op
 * @property {FieldSpec[]} args
 */
var OrderField = schema({
  field: Array.of(Literal),
  direction: [null, 'ASC', 'DESC'],
});

/**
 * @typedef {Object} Recommender
 * @property {string} id
 * @property {string} name
 */

/**
 * @typedef {Object} RuleSpec
 * @property {Operand[]} [select]
 * @property {RuleSpec} [from]
 * @property {Operand} where
 * @property {FieldSpec[]} [groupBy]
 * @property {AggregateField[]} [aggregate]
 * @property {Operand} [having]
 * @property {OrderField[]} [orderBy]
 * @property {number} [offset]
 * @property {number} [limit]
 */
var RuleSpec = schema({
  select: [null, Array.of(Operand)],
  from: [null, schema.self],
  where: Operand,
  groupBy: [null, Array.of(FieldSpec)],
  aggregate: [null, Array.of(AggregateField)],
  having: [null, Operand],
  orderBy: [null, Array.of(OrderField)],
  offset: [null, Number],
  limit: [null, Number],
});

/**
 * @typedef {Object} BehaviorEventSpec
 * @property {string} name
 * @property {string} [type] - defaults to "default" if not present
 * @property {string} [category] - defaults to "Other" if not present
 * @property {Object} [tags]
 * @property {number} [revenue]
 */

var BehaviorEventSpec = schema({
  name: String,
  type: [null, String],
  category: [null, String],
  tags: [null, Object],
  revenue: [null, Number],
});

/**
 * Compact EventBase wire format.
 * @typedef {Object} CompactEB
 * @property {string} n - name
 * @property {string} y - type
 * @property {string} [c] - category
 * @property {number} [r] - revenue
 * @property {Object} [o] - options
 */

/**
 * schema
 * @private
 */
var CompactEB = schema({
  n: String,
  y: String,
  c: [null, String],
  r: [null, Number],
  o: [null, Object],
});

/**
 * Compact timestamp wire format.
 * @typedef {Object} CompactTS
 * @property {number} d - delta time
 * @property {number} i - index in sorted order of timestamp
 */

/**
 * schema
 * @private
 */
var CompactTS = schema({
  d: Number,
  i: Number,
});

/**
 * Compact Event wire format.
 * @typedef {Object} BehaviorCompactEvent
 * @property {CompactEB} eb - event base
 * @property {string} h - hash of event base
 * @property {number} tb - time base (timestamp of earliest event with this event base).
 * @property {CompactTS[]} ts - List of timestamp deltas.
 */

var BehaviorCompactEvent = schema({
  eb: CompactEB,
  h: String,
  tb: Number,
  ts: Array.of(CompactTS),
});

/**
 * An Event in the behavioral store (legacy verbose format).
 *
 * @typedef {Object} BehaviorVerboseEvent
 * @property {number} t Timestamp when event happened
 * @property {string} y Event type
 * @property {string} [n] Event name
 * @property {number} [r] Revenue
 * @property {string} [s] Session ID
 * @property {Object} [o] Tags (options set by user)
 */

/**
 * Schema for BehaviorVerboseEvent
 * @private
 */
var BehaviorVerboseEvent = schema({
  t: Number,
  y: String,
  s: [null, String],
  n: [null, String],
  r: [null, Number],
  o: [null, Object],
});

/**
 * Stored events may be either BehaviorCompactEvents or legacy BehaviorVerboseEvents.
 * @private
 */
var BehaviorStoredEvents = schema([
  Array.of(BehaviorVerboseEvent),
  Array.of(BehaviorCompactEvent),
]);

/**
 * High-level rule format v0.1.
 *
 * Used for the legacy `getEvents` and `getByFrequency` APIs
 *
 * Used for Visitor Behavior audience conditions, in which case the `pick` and
 * `count` clauses must be present to ensure that the final result is a boolean.
 *
 * @typedef {Object} RuleSpecV0_1
 * @property {Object} [action]
 * @property {string} [action.type]
 * @property {string} action.value
 * @property {Object} [count]
 * @property {string} count.comparator
 * @property {string} count.value
 * @property {RuleSpecFilterV0_1[]} [filters]
 * @property {Object} [pick]
 * @property {string} [pick.name]
 * @property {string} [pick.modifier]
 * @property {number} [pick.count]
 * @property {string} [source]
 * @property {Object} [time]
 * @property {string} time.type
 * @property {number} [time.days]
 * @property {number} [time.start]
 * @property {number} [time.stop]
 * @property {string} version
 */
var RuleSpecV0_1 = schema({
  action: [null, {
    type: [null, String],
    value: String,
  }],
  count: [null, {
    comparator: String,
    value: Number,
  }],
  filters: [null, Array.of(RuleSpecFilterV0_1)],
  pick: [null, {
    name: [null, String],
    modifier: [null, String],
    count: [null, Number],
  }],
  source: [null, String],
  time: [null, {
    type: String,
    days: [null, Number],
    start: [null, Number],
    stop: [null, Number],
  }],
  version: '0.1',
});

/**
 * A filter in rule format v0.1.
 * @typedef {Object} RuleSpecFilterV0_1
 * @property {string|string[]} name
 * @property {string} [datatype]
 * @property {string} [modifier]
 * @property {string} comparator
 * @property {Number|string|Array} value
 */
var RuleSpecFilterV0_1 = schema({
  name: [String, Array.of(String)],
  datatype: [null, String],
  modifier: [null, String],
  comparator: String,
  value: [Number, String, Array]
});

/**
 * High-level rule format v0.2, documented here:
 * https://confluence.sso.episerver.net/display/EXPENG/High-Level+Behavioral+Rule+Format+v0.2
 *
 * Used for the `query` API.
 *
 * Used for Custom Behavioral Attribute `rule_json`, in which case the `pick`
 * and `reduce` clauses must be present to ensure that the final result is a
 * single number or string.
 *
 * @typedef {Object} RuleSpecV0_2
 * @property {RuleSpecFilterV0_2[]} [filter]
 * @property {RuleSpecSortV0_2[]} sort
 * @property {Object} [pick]
 * @property {RuleSpecFieldV0_2} pick.field
 * @property {number} [limit]
 * @property {Object} [reduce]
 * @property {string} reduce.aggregator
 * @property {number} [reduce.nth]
 * @property {string} version
 */
var RuleSpecV0_2 = schema({
  filter: [null, Array.of(RuleSpecFilterV0_2)],
  sort: [null, Array.of(RuleSpecSortV0_2)],
  pick: [null, {
    field: RuleSpecFieldV0_2,
  }],
  limit: [null, Number],
  reduce: [null, {
    // TODO(nikhil): Use the rule engine's AggregateOperator for most of these?
    // 'min', 'max', 'avg', and 'sum' only apply to numbers that have been picked.
    aggregator: ['count', 'nth', 'min', 'max', 'avg', 'sum'],
    // Required for the 'nth' aggregator.
    n: [null, Number],
  }],
  version: '0.2',
});

/**
 * A filter in rule format v0.2.
 * @typedef {Object} RuleSpecFilterV0_2
 * @property {RuleSpecFieldV0_2} field
 * @property {string} [comparator]
 * @property {Number|string|Array} [value]
 */
var RuleSpecFilterV0_2 = schema({
  field: RuleSpecFieldV0_2,
  // Defaults to 'eq'.
  comparator: [null, Comparator],
  // Optional for the 'exists' comparator.
  value: [null, Number, String, Array],
});

/**
 * A sort order in rule format v0.2.
 * @typedef {Object} RuleSpecSortV0_2
 * @property {string[]} field
 * @property {string} direction
 */
var RuleSpecSortV0_2 = schema({
  // ['frequency'] requires a pick clause and nullifies all other sort orders.
  field: [Array.like(['time']), Array.like(['frequency'])],
  direction: ['ascending', 'descending'],
});

/**
 * A field identifier in rule format v0.2.
 * @typedef {string[]} RuleSpecFieldV0_2
 */
var RuleSpecFieldV0_2 = schema([
  Array.like(['name']),
  Array.like(['type']),
  Array.like(['category']),
  Array.like(['tags', String]),
  Array.like(['time']),
  Array.like(['age']),
]);

/**
 * @typedef {Object} Action
 * @property {string} id
 * @property {string} pageId
 * @property {string} variationId
 * @property {string} experimentId
 * @property {Change[]} changes
 */

/**
 * @typedef {Array} Conditions
 */

/**
 * @typedef {Object} Condition
 * @property {string} type
 * @property {string} [name]
 * @property {string} [match]
 * @property {*} [value]
 */

/**
 * Schema for conditionOperator
 * @private
 */
var conditionOperator = schema(['and', 'or', 'not']);

/**
 * Schema for Conditions
 * @private
 */
var Conditions = schema(Array.of([
  conditionOperator,
  {
    type: String,
    '?match': [null, String],
    '?name': [null, String],
    '?value': undefined,
  },
  // TODO(tyler): remove this once above is valid
  Object, String,
  schema.self,
]));

/**
 * Return whether a "condition atom" (object) is satisfied.
 *
 * @callback evaluatorFn
 * @param {Condition} condition Condition to check
 * @returns {Boolean|undefined} Whether condition is satisfied
 */

/**
 * @typedef {Object} Audience
 * @property {string} id
 * @property {string} [name]
 * @property {Conditions} [conditions]
 */

/**
 * Schema for Audience
 * @private
 */
var Audience = schema({
  id: String,
  name: [null, String],
  conditions: [null, Conditions],
});

/**
 * An int in the range [0, 10000]
 *
 * @typedef {number} BasisPoints
 */

/**
 * @typedef {Object} WeightDistribution
 * @property {string} entityId ID of corresponding entity for range.
 * @property {BasisPoints} endOfRange One more than the greatest bucketing number which maps to this range
 *                                    (lower bound is defined by the previous WeightDistribution in an array).
 */

/**
 * Schema for WeightDistribution
 * @private
 */
var WeightDistribution = schema({
  entityId: String,
  endOfRange: BasisPoints,
});


/**
 * @typedef {object} AttributeChange
 * @property {string} id
 * @property {string} type "attribute"
 * @property {string} selector Element selector
 * @property {AttributeChanges} attributes
 * @property {Object} css Element CSS changes - this will be combined with an element's inline styles
 * @property {StringOrNumber[]} [dependencies] Array of change ids
 */

/**
 * @typedef {object} AttributeChanges
 * @property {string} [class] Replacement .className
 * @property {string} [hide] Sugar for the visibility=hidden style
 * @property {string} [href] Replacement .href
 * @property {string} [html] Replacement .innerHTML
 * @property {string} [remove] Sugar for the display=none style
 * @property {string} [src] Replacement .src
 * @property {string} [style] Inline CSS from editor textarea - this will be combined with css changes and replace an element's original inline styles
 * @property {string} [text] Replacement .textContent
 */

/**
 * @typedef {object} AppendChange
 * @property {string} id
 * @property {string} type "append"
 * @property {string} selector Selector of element to add last child to
 * @property {string} value HTML or text to append
 * @property {StringOrNumber[]} [dependencies] Array of change ids
 */

/**
 * @typedef {object} CustomCodeChange
 * @property {string} id
 * @property {string} type "custom_code"
 * @property {function} value Anonymous function wrapping custom code
 * @property {StringOrNumber[]} [dependencies] Array of change ids
 */

/**
 * @typedef {object} RearrangeChange
 * @property {string} selector Selector of an element to rearrange
 * @property {string} insertSelector Selector of an element where to insert change
 * @property {string} operator Action to do - one of "APPEND"|"PREPEND"|"BEFORE"|"AFTER"
 */

/**
 * @typedef {object} RedirectChange
 * @property {string} id
 * @property {string} type "redirect"
 * @property {boolean} [allowAdditionalRedirect] If true - allow redirect change to execute even if user
 *                      came to this page using redirect change. False by default.
 * @property {boolean} [preserveParameters] If we should copy current page parameters to redirect URL.
 *                      False by default.
 * @property {boolean} [hidePage] If we need to hide page body to prevent flashing. True by default.
 * @property {function|string} dest URL or function returning a URL to redirect to
 */

/**
 * @typedef {object} ChangeStatusMap Map of change id to change status
 */

/**
 * @typedef {object} ChangeStatus Individual change status for each change in ChangeStatusMap
 * @property {string} status
 * @property {Error} [error] Error object generated if evaluation of change failed or null
 */

/**
 * @typedef {object} ChangeApplier Represents an object that does single change to a page.
 * @property {Function} apply
 * @property {Function} [cancel] Will be called if the view is deactivated and doesn't specify undoOnDeactivation.
 * @property {Function} [undo] Will be called if the view is deactivated and does specify undoOnDeactivation.
 */

/**
 * @callback selectExperiment
 * @param {Layer} layer
 * @param {string[]} audienceIds
 * @param {string} bucketingId
 * @returns {Experiment}
 * @throws {DecisionError}
 */

/**
 * @callback includePageIdInDecisionTicket
 * @param {Layer} layer
 * @returns {Boolean}
 */

/**
 * @callback decideLayer
 * @param {Layer} layer
 * @param {DecisionTicket} decisionTicket
 * @returns {Object} decision with optional experiment, variation, and error properties
 * @throws {DecisionError}
 */

/**
 * @typedef {Object} Decider Module that partially controls bucketing decisions.
 * @property {Boolean} includePageIdInDecisionTicket Specifies when the pageId should
 * be included in the decisionTicket
 * @property {selectExperiment} [selectExperiment]
 * @property {decideLayer} [decideLayer]
 */

/**
 * @typedef {object} ChangesetApplier Represents a group of ChangeApplier objects that
 *                    can be applied/cancelled together
 */

/**
 * The specification of a change that the customer wants us to apply.
 *
 * @typedef {Object} Change
 * @property {StringOrNumber} id
 * @property {string} [src]
 * @property {string} [type]
 * @property {StringOrNumber[]} [dependencies]
 * @property {string} [selector]
 * @property {Object} attributes
 */

var ChangeProperties = {
  id: [String, Number],
  src: [null, String],
  type: [null, String],
  dependencies: [null, Array.of([String, Number])],
};

/**
 * Schema for Change
 * @private
 */
var Change = schema(ChangeProperties);

/**
 * @typedef {Object} PreparedChange
 * @property {StringOrNumber} id
 * @property {Object} changeApplier
 * @property {Function} changeApplier.apply Returns a promise
 */

/**
 * @private
 */
var PreparedChange = schema({
  id: [String, Number],
  changeApplier: Object,
});

/**
 * Input (pre-processed) data format for Variation actions (in ClientData)
 * @typedef {Object} Action
 * @property {string} [pageId]
 * @property {string} [viewId] Backwards compatibility alias of pageId
 * @property {Array.<Change|string>} changes
 */

/**
 * Schema for Action
 * @private
 */
var Action = schema({
  pageId: [null, String],
  viewId: [null, String],
  changes: Array.of([Change, String]),
});

/**
 * @typedef {Object} CampaignAction
 * @property {string} pageId
 * @property {Change[]} changes
 */

/**
 * Schema for CampaignAction
 * @private
 */
var CampaignAction = schema({
  pageId: String,
  changes: Array.of(Change),
});

/**
 * Processed/transformed action data. Combines information from the Action itself
 * with information from where it is applied.
 *
 * @typedef {Object} ActionData
 * @property {string} id
 * @property {string} type
 * @property {string} layerId
 * @property {string} [experimentId]
 * @property {string} [variationId]
 * @property {string} [pageId]
 * @property {Array.<Change|string>} changeSet List of change ID (String) or Change objects
 */

/**
 * @private
 */
var ActionData = schema({
  id: String,
  type: String,
  layerId: String,
  experimentId: [null, String],
  variationId: [null, String],
  pageId: [null, String],
  changeSet: Array.of([Change, String]),
});

/**
 * The second arg to a change applier constructor - may vary from application to application.
 *
 * @typedef {Object} ChangeConfig
 * @property {string} identifier
 * @property {ActionData} [action]
 * @property {number} startTime Date when action was started
 */

/**
 * @private
 */
var ChangeConfig = schema({
  identifier: [String, Number],
  // Until the editor passes an ActionData like object via editor/change_applier.create,
  // we must allow action to be passed as null.
  // TODO(james): Update the editor to pass an ActionData like object
  //              and consume editor/change_applier.executePreparedAction (WEB-1949)
  action: [null, ActionData],
  startTime: Number,
});

/**
 * @typedef {Object} Variation
 * @property {string} id
 * @property {string} [name]
 * @property {Object.<string, Change[]>} actions Mapping of pageId to changes (see [client-change-applier](https://github.com/optimizely/client-change-applier))
 */

/**
 * Schema for Variation
 * @private
 */
var Variation = schema({
  id: String,
  name: [null, String],
  actions: Array.of(Action),
});

/**
 * @typedef {Object} CampaignVariation
 * @property {string} id
 * @property {string} [name]
 * @property {Array.<CampaignAction>} actions
 */

/**
 * Schema for CampaignVariation
 * @private
 */
var CampaignVariation = schema({
  id: String,
  name: [null, String],
  actions: Array.of(CampaignAction),
});

/**
 * @typedef {Object} Experiment
 * @property {string} id
 * @property {string} [name]
 * @property {string[]} audienceIds
 * @property {Variation[]} variations
 * @property {Object} [integrationSettings]
 * @property {WeightDistribution[]} [weightDistributions]
 * @property {string} [bucketingStrategy]
 * @property {Array.<Change|string>} [changes]
 * @property {ExperimentMetadata} [experimentMetadata]
 */

/**
 * Schema for Experiment
 * @private
 */
var Experiment = schema({
  id: String,
  name: [null, String],
  audienceIds: [null, Array.of([String, Conditions])],
  variations: Array.of(Variation),
  integrationSettings: [null, Object],
  weightDistributions: [null, Array.of(WeightDistribution)],
  bucketingStrategy: [null, 'impression', 'lifetime'],
  changes: [null, Array.of([Change, String])],
  deployed: [null, Boolean],
});

/**
 * @typedef {Object} CampaignExperiment
 * @property {string} id
 * @property {string} [name]
 * @property {string[]} audienceIds
 * @property {CampaignVariation[]} variations
 * @property {Object} [integrationSettings]
 * @property {WeightDistribution[]} [weightDistributions]
 * @property {string} [bucketingStrategy]
 * @property {Array.<Change|string>} [changes]
 */

/**
 * Schema for CampaignExperiment
 * @private
 */
var CampaignExperiment = schema({
  id: String,
  name: [null, String],
  audienceIds: [null, Array.of([String, Conditions])],
  variations: Array.of(CampaignVariation),
  integrationSettings: [null, Object],
  weightDistributions: [null, Array.of(WeightDistribution)],
  bucketingStrategy: [null, 'impression', 'lifetime'],
  changes: [null, Array.of([Change, String])],
});

/**
 * @typedef {Object} LayerActivation
 * @property {number} [override] timeout for pending audience evaluation
 */

/**
 * schema
 * @private
 */
var LayerActivation = schema({
  timeout: [null, Number],
});

/**
 * @typedef {Object} Layer
 * @property {string} id
 * @property {string} [name]
 * @property {string} [commitId]
 * @property {string} [groupId]
 * @property {number} holdback
 * @property {Object} [integrationSettings]
 * @property {number} integrationStringVersion
 * @property {string} policy
 * @property {LayerActivation} [activation]
 * @property {Experiment[]} experiments
 * @property {string[]} [pageIds]
 * @property {string[]} [viewIds] Backwards-compatibility alias for pageIds
 * @property {Array.<Change|string>} [changes]
 * @property {Object} [decisionMetadata]
 * @property {WeightDistribution[]} [weightDistributions]
 */

/**
 * Schema for Layer
 * @private
 */
var Layer = schema({
  id: String,
  name: [null, String],
  commitId: [null, String],
  groupId: [null, String],
  holdback: BasisPoints,
  integrationSettings: [null, Object],
  integrationStringVersion: Number,
  policy: [null].concat(_.toArray(layerPolicies.LayerPolicies)),
  activation: [null, LayerActivation],
  experiments: Array.of(Experiment),
  pageIds: [null, Array.of(String)],
  viewIds: [null, Array.of(String)],
  changes: [null, Array.of([Change, String])],
  decisionMetadata: [null, Object],
  weightDistributions: [null, Array.of(WeightDistribution)],
  concluded: [null, Boolean],
});

/**
 * @typedef {Object} Campaign
 * @property {string} id
 * @property {string} [name]
 * @property {string} [commitId]
 * @property {string} [groupId]
 * @property {number} holdback
 * @property {Object} [integrationSettings]
 * @property {number} integrationStringVersion
 * @property {string} policy
 * @property {LayerActivation} [activation]
 * @property {CampaignExperiment[]} experiments
 * @property {string[]} pageIds
 * @property {Array.<Change|string>} [changes]
 * @property {Object} [decisionMetadata]
 * @property {WeightDistribution[]} [weightDistributions]
 */

/**
 * Schema for Layer
 * @private
 */
var Campaign = schema({
  id: String,
  name: [null, String],
  commitId: [null, String],
  groupId: [null, String],
  holdback: BasisPoints,
  integrationSettings: [null, Object],
  integrationStringVersion: Number,
  policy: [null].concat(_.toArray(layerPolicies.LayerPolicies)),
  activation: [null, LayerActivation],
  experiments: Array.of(CampaignExperiment),
  pageIds: Array.of(String),
  changes: [null, Array.of([Change, String])],
  decisionMetadata: [null, Object],
  weightDistributions: [null, Array.of(WeightDistribution)],
});

/**
 * @typedef {Object} Group
 * @property {string} id
 * @property {string} [name]
 * @property {WeightDistribution[]} [weightDistributions]
 */

/**
 * Schema for Group
 * @private
 */
var Group = schema({
  id: String,
  name: [null, String],
  weightDistributions: Array.of(WeightDistribution),
});

/**
 * @typedef {Object} SelectorFilter
 * @property {string} filterType "target_selector"
 * @property {string} selector
 */

/**
 * Schema for SelectorFilter
 * @private
 */
var SelectorFilter = schema({
  filterType: eventFilterTypes.TARGET_SELECTOR,
  selector: String,
});

/**
 * @typedef {SelectorFilter} EventFilter
 */

/**
 * @typedef {Object} EventSchema
 * @property {string} id
 * @property {string} apiName
 * @property {string} [category]
 * @property {string} [pageId] Null in the case of custom events
 * @property {string} [viewId] Backwards-compatibility alias of pageId
 * @property {string} [name]
 * @property {string} eventType
 * @property {EventFilter} eventFilter
 */

/**
 * Schema for EventSchema
 * @private
 */
var EventSchema = schema({
  id: String,
  pageId: [null, String],
  viewId: [null, String],
  name: [null, String],
  apiName: String,
  // TODO (asa): supply category options via categories.yaml
  category: String,
  eventType: String,
  eventFilter: [null, SelectorFilter],
});

/**
 * @callback pollingActivationCode
 */

/**
 * @callback callbackActivationCode
 */

/**
 * @typedef {Object} View
 * @property {string} id
 * @property {string} [name]
 * @property {Conditions} [staticConditions]
 * @property {ViewTag[]} [tags]
 * @property {string|undefined} [activationType]
 * @property {pollingActivationCode|callbackActivationCode} [activationCode]
 * @property {Boolean} [deactivationEnabled]
 * @property {Boolean} [undoOnDeactivation]
 */

/**
 * Schema for View
 * @private
 */
var View = schema({
  id: String,
  name: [null, String],
  apiName: String,
  // TODO (asa): supply category options via categories.yaml
  category: String,
  staticConditions: [null, Conditions],
  tags: [null, Array.of(ViewTag)],
  activationType: [null].concat(_.toArray(_.values(enums.ViewActivationTypes))),
  activationCode: [null, Function],
  deactivationEnabled: [null, Boolean],
  undoOnDeactivation: [null, Boolean],
});

/**
 * @typedef {Object} Page
 * @property {string} id
 * @property {string} [name]
 * @property {Conditions} [staticConditions]
 * @property {ViewTag[]} [tags]
 * @property {string} [activationType]
 * @property {pollingActivationCode|callbackActivationCode} [activationCode]
 * @property {Boolean} [deactivationEnabled]
 * @property {Boolean} [undoOnDeactivation]
 */

/**
 * Schema for Page
 * @private
 */
var Page = schema({
  id: String,
  name: [null, String],
  apiName: String,
  // TODO (asa): supply category options via categories.yaml
  category: String,
  staticConditions: [null, Conditions],
  tags: [null, Array.of(PageTag)],
  activationType: [null].concat(_.toArray(enums.ViewActivationTypes)),
  activationCode: [null, Function],
  deactivationEnabled: [null, Boolean],
  undoOnDeactivation: [null, Boolean],
});

/**
 * @typedef {Object} Dimension
 * @property {string} id Name of the feature ("dimension_id" property value for custom dimensions)
 * @property {string} [apiName] API name of the feature
 * @property {string} [segmentId] segment_id associated with the custom dimension, if any
 */

/**
 * schema
 */
var Dimension = schema({
  id: String,
  name: [null, String],
  apiName: [null, String],
  segmentId: [null, String],
});

/**
 * @typedef {Object} FeatureValue
 * @property {string} name
 * @property {string} type
 * @property {string} [id]
 * @property {Boolean} shouldIndex
 * @property {string|Number|Boolean} value Current value for feature for visitor
 */

/**
 * schema
 * @private
 */
var FeatureValue = schema({
  name: String,
  type: String,
  id: [null, String],
  shouldIndex: Boolean,
  value: [String, Number, Boolean],
});

/**
 * @typedef {Object} IntegrationSettings
 * @property {string} id
 * @property {string} [url]
 * @property {string} [cbParam]
 * @property {string} [dataFetchMethod]
 */

/**
 * schema
 * @private
 */
var IntegrationSettings = schema({
  id: String,
  url: [null, String],
  cbParam: [null, String],
  dataFetchMethod: [null, String],
});

/**
 * @typedef {Object} DCPKeyfieldLocator
 * @property {string} dcp_datasource_id
 * @property {Boolean} [is_optimizely]
 * @property {string} [name] Will be undefined if type == 'uid'.
 * @property {string} type
 */

/**
 * schema
 * @private
 */
var DCPKeyfieldLocator = schema({
  dcp_datasource_id: String,
  is_optimizely: [null, Boolean],
  name: [null, String],
  type: String,
});

/**
 * Note: attributeId xor attributeName must be specified.
 *
 * @typedef {Object} VisitorAttributeDescriptor
 * @property {string|Number} [datasourceId] Only applies to DCP attributes.
 * @property {string|Number} [attributeId]
 * @property {string} [attributeName]
 */

/**
 * For public API functions that deal with specific DCP attributes.
 * Note: attributeId xor attributeName must be specified.
 *
 * @private
 */
var VisitorAttributeDescriptor = schema({
  datasourceId: [null, String, Number],
  attributeId: [null, String, Number],
  attributeName: [null, String],
});

/**
 * @typedef {Object} VisitorAttribute
 * @property {string} id
 * @property {string} datatype
 * @property {string} [dcp_datasource_id]
 * @property {string} [name] Will be undefined if the project uses obfuscation.
 * @property {string} [rule_json] Defined iff this is a CustomBehavioralAttribute.
 */

/**
 * schema
 * @private
 */
var VisitorAttribute = schema({
  id: String,
  datatype: String,
  dcp_datasource_id: [null, String],
  name: [null, String],
  rule_json: [null, String],
});

/**
 * @typedef {Object} ListTargetingKey
 * @property {module:enums~ListTargetingKeyTypes} type
 * @property {string} key Name of key to get
 */

var ListTargetingKey = schema({
  type: _.toArray(enums.ListTargetingKeyTypes),
  key: String,
});

/**
 * base "schema"
 * @private
 */
var BaseDCPRequestDescriptor = {
  uid: String,
  currentAliases: {
    '*': String,
  },
};

/**
 * @typedef {Object} DCPSnippetRequestDescriptor
 * @property {string} uid The Optimizely end user id.
 * @property {Object.<string>} currentAliases Other data source IDs to send to Vissandara
 *
 * @property {string} snippetId
 * @property {string} dcpServiceId
 */

/**
 * Schema
 * @private
 */
var DCPSnippetRequestDescriptor = schema(_.extend({}, BaseDCPRequestDescriptor, {
  snippetId: String,
  dcpServiceId: String,
}));

/**
 * @typedef {Object} DCPProjectRequestDescriptor
 * @property {string} uid The Optimizely end user id.
 * @property {Object.<string>} currentAliases Other data source IDs to send to Vissandara if !isAlreadyAliased.
 *
 * @property {string} projectId
 * @property {string} optlyDatasourceId The Optimizely datasource id.
 */

/**
 * schema
 * @private
 */
var DCPProjectRequestDescriptor = schema(_.extend({}, BaseDCPRequestDescriptor, {
  projectId: String,
  optlyDatasourceId: String,
}));

/**
 * @typedef {Object} Recommender
 * @property {string} id
 * @property {string} servingUrl The URL end-point that serves recommendations.
 */

/**
 * schema
 * @private
 */
var Recommender = schema({
  id: String,
  servingUrl: String,
});

/**
 * @typedef {Object} RecommenderService
 * @property {string} id
 * @property {string} [idTagName] Name of each catalog item's id tag.
 * @property {string} [catalogUrl] Url to fetch item metadata from.
 * @property {Recommender[]} [recommenders] List of recommenders available under this service.
 * @property {number} serviceVersion Version of the service.
 */

/**
 * schema
 * @private
 */
var RecommenderService = schema({
  id: String,
  idTagName: [null, String],
  catalogUrl: [null, String],
  recommenders: [null, Array.of(Recommender)],
  serviceVersion: Number,
});

/**
 * @callback PluginFn
 * @param {Object} PluginManager plugin_manager API
 */

/**
 * @typedef {Object} ClientData
 * @property {string} [accountId]
 * @property {Audience[]} [audiences]
 * @property {Change[]} [changes]
 * @property {Dimension[]} [dimensions]
 * @property {EventSchema[]} [events]
 * @property {BasisPoints} [holdback]
 * @property {Group[]} [groups]
 * @property {Layer[]} [layers]
 * @property {string[]} [listTargetingCookies] (deprecated, see [CJS-2060](https://optimizely.atlassian.net/browse/CJS-2060))
 * @property {ListTargetingKey[]} [listTargetingKeys]
 * @property {string} [dcpServiceId]
 * @property {DCPKeyfieldLocator[]} [dcpKeyfieldLocators]
 * @property {VisitorAttribute[]} [visitorAttributes]
 * @property {VisitorIdLocator} [visitorIdLocator]
 * @property {string} projectId
 * @property {string} [snippetId]
 * @property {string} namespace Visitor data will be stored within this namespace.
 * @property {Function} [projectJS]
 * @property {View[]} [views]
 * @property {RecommenderService[]} [recommenderServices]
 * @property {string} [revision]
 * @property {PluginFn[]} [plugins]
 * @property {Boolean} [anonymizeIP] Whether the client IP address should be scrubbed in tracker data
 * @property {Boolean} [enableForceParameters]
 * @property {Object.<boolean, String>} [experimental]
 * @property {InterestGroup[]} [interestGroups]
 * @property {TagGroup[]} [tagGroups]
 */

/**
 * Schema for ClientData
 * @private
 */
var ClientData = schema({
  accountId: [null, String],
  audiences: [null, Array.of(Audience)],
  changes: [null, Array.of(Change)],
  dimensions: [null, Array.of(Dimension)],
  events: [null, Array.of(EventSchema)],
  holdback: [null, BasisPoints],
  integrationSettings: [null, Array.of(IntegrationSettings)],
  interestGroups: [null, Array.of(InterestGroup)],
  groups: [null, Array.of(Group)],
  layers: [null, Array.of(Layer)],
  listTargetingCookies: [null, Array.of(String)],
  listTargetingKeys: [null, Array.of(ListTargetingKey)],
  dcpServiceId: [null, String],
  dcpKeyfieldLocators: [null, Array.of(DCPKeyfieldLocator)],
  visitorAttributes: [null, Array.of(VisitorAttribute)],
  visitorIdLocator: [null, VisitorIdLocator],
  namespace: String,
  projectId: String,
  snippetId: [null, String],
  projectJS: [null, Function],
  views: [null, Array.of(View)],
  recommenderServices: [null, Array.of(RecommenderService)],
  revision: [null, String],
  tagGroups: [null, Array.of(TagGroup)],
  plugins: [null, Array.of(Function)],
  anonymizeIP: [null, Boolean],
  enableForceParameters: [null, Boolean],
  experimental: [null, {
    '*': [Boolean, String],
  }],
  '*': null,
});

/**
 * @typedef {Object} SessionState
 * @property {string} [sessionId]
 * @property {number} lastSessionTimestamp
 */

/**
 * schema
 * @private
 */
var SessionState = schema({
  sessionId: [null, String],
  lastSessionTimestamp: Number,
});

/**
 * @typedef {Object} ViewTag
 * @property {string} name
 * @property {string|Function} locator
 * @property {string} locatorType
 * @property {string} valueType
 */

/**
 * Schema for ViewTag
 * @private
 */
var ViewTag = schema({
  apiName: String,
  // TODO (asa): supply category options via categories.yaml
  category: String,
  locator: [String, Function],
  locatorType: _.toArray(TagEnums.locatorType),
  valueType: _.toArray(TagEnums.valueType),
});

/**
 * @typedef {Object} InterestGroup
 * @property {string} id
 * @property {Array.<String>} keywords
 * @property {string} tagGroupId
 * @property {Boolean} available
 */

/**
 * Schema for InterestGroup
 * @private
 */
var InterestGroup = schema({
  id: String,
  keywords: Array.of(String),
  tagGroupId: String,
  available: Boolean,
});

/**
 * @typedef {Object} TagDescriptor
 * @property {string} viewId
 * @property {string} tagApiName
 */

/**
 * Schema for TagDescriptor
 * @private
 */
var TagDescriptor = schema({
  viewId: String,
  tagApiName: String,
});

/**
 * @typedef {Object} TagGroup
 * @property {string} id
 * @property {Array.<TagDescriptor>} tags
 */

/**
 * Schema for TagGroup
 * @private
 */
var TagGroup = schema({
  id: String,
  tags: Array.of(TagDescriptor),
});

/**
 * @typedef {Object} PageTag
 * @property {string} name
 * @property {string} locator
 * @property {string} locatorType
 * @property {string} valueType
 */

/**
 * Schema for PageTag
 * @private
 */
var PageTag = schema({
  apiName: String,
  // TODO (asa): supply category options via categories.yaml
  category: String,
  locator: String,
  locatorType: _.toArray(TagEnums.locatorType),
  valueType: _.toArray(TagEnums.valueType),
});

/**
 * @typedef {Object} AnalyticsTracker - a set of tracking hooks and corresponding timing directives
 *
 * Timing directives for the trackLayerDecision hook, if defined.
 * See https://confluence.sso.episerver.net/display/EXPENG/Design+Doc%3A+improved+redirect+tracking
 * @property {string} [preRedirectPolicy] - whether/when trackLayerDecision should be called on pre-redirect pages.
 * @property {string} [postRedirectPolicy] - whether/when trackLayerDecision should be called on post-redirect pages.
 * @property {string} [nonRedirectPolicy] - whether/when trackLayerDecision should be called for non-redirect decisions.
 *
 * Tracking hooks.
 * @property {Function} [trackLayerDecision] - new-style hook for decision events.
 * @property {Function} [onClickEvent]
 * @property {Function} [onPageActivation]
 * @property {Function} [onCustomEvent]
 * @property {Function} [onClientActivation]
 * @property {Function} [serializeSettings] - Serialize settings to string (for relay). If undefined, no settings will be relayed.
 * @property {Function} [deserializeSettings] - Deserialize settings from string (for relay).
 *  If undefined, no settings will be loaded, even if relayed.
 *  In addition, post-redirect tracking will not occur (in spite of whatever postRedirectPolicy is defined)
 */

/**
 * schema for AnalyticsTracker
 * @private
 */
var AnalyticsTracker = schema({
  preRedirectPolicy: [null].concat(_.toArray(trackingPolicies.PreRedirectPolicies)),
  postRedirectPolicy: [null].concat(_.toArray(trackingPolicies.PostRedirectPolicies)),
  nonRedirectPolicy: [null].concat(_.toArray(trackingPolicies.NonRedirectPolicies)),
  // TODO: replace each Function with customized typedefs so that we can describe the various params.
  trackLayerDecision: [null, Function],
  onClickEvent: [null, Function],
  onPageActivation: [null, Function],
  onCustomEvent: [null, Function],
  onClientActivation: [null, Function],
  serializeSettings: [null, Function],
  deserializeSettings: [null, Function],
});

/**
 * @typedef {string} AnalyticsTrackingHook
 */

/**
 * Schema for valid AnalyticsTracking hooks
 * @private
 */
var AnalyticsTrackingHook = schema([
  'trackLayerDecision',
  'onClickEvent',
  'onPageActivated',
  'onCustomEvent',
  'onClientActivation',
]);

/**
 * @typedef {string} ValueType
 */

/**
 * Schema for ValueType enum
 * @private
 */
var ValueType = schema(_.toArray(TagEnums.valueType));

/**
 * @typedef {string} AttributionTypeSchema
 */

/**
 * Schema for AttributionTypeSchema enum
 * @private
 */
var AttributionTypeSchema = schema(_.toArray(enums.AttributionTypes));

// types for analytics callbacks

/**
 * @callback trackLayerDecision
 * @param {DecisionData} arg1
 */

/**
 * @callback onClickEvent
 * @param {ClickEventData} arg1
 */

/**
 * @callback onPageActivated
 * @param {ViewEventData} arg1
 */

/**
 * @callback onCustomEvent
 * @param {CustomEventData} arg1
 */

/**
 * @callback onClientActivation
 * @param {ClientActivationEventData} arg1
 */

/**
 * @typedef {Object} CookieOptions
 * @param {string} [path]
 * @param {string} [domain]
 * @param {number} [maxAge] Seconds after which cookie expires
 * @param {Date} [expires] Date when cookie expires
 * @param {boolean} [secure]
 * @param {Boolean} [encodeValue] Whether to automatically encode the value before setting. Defaults to true.
 */

var CookieOptions = schema({
  path: [null, String],
  domain: [null, String],
  maxAge: [null, Number],
  expires: [null, Date],
  secure: [null, Boolean],
  encodeValue: [null, Boolean],
});

/**
 * Response from cdn3 geolocation service
 *
 * @typedef {Object} IPGeo
 * @property {string} [ip]
 * @property {Object} [location]
 * @property {string} [location.continent]
 * @property {string} [location.country]
 * @property {string} [location.region]
 * @property {string} [location.city]
 * @property {string} [location.dma] Nielsen Designated Market Area
 */

/**
 * schema for GeoLocation
 * @private
 */
var GeoLocation = schema({
  continent: [null, String],
  country: [null, String],
  region: [null, String],
  city: [null, String],
  dma: [null, String]
});

/**
 * schema for IPGeo data
 * @private
 */
var IPGeo = schema({
  ip: [null, String],
  location: [null, GeoLocation],
});

/**
 * Response from ODDS
 *
 * @typedef {Object} ListData
 * @property {Object.<boolean>} lists
 */

/**
 * schema for ListData
 * @private
 */
var ListData = schema({
  lists: {
    '*': Boolean
  },
});

/**
 * Response from DCP
 *
 * @typedef {Object} DCPData
 * @property {Object.<string>} [aliases]
 *   Maps datasource IDs (strings) to the visitor's IDs in those datasources (strings)
 *   at the time this DCPData was returned from Vissandara.
 * @property {Object.<Object>} [attributes] Map of datasource ID to map of attribute ID to Attribute value
 * @property {string[]} [conditionValues] Lists matching rules (JSON strings).
 * @property {Object.<boolean>} [conditionValuesResults] Maps rules (JSON strings) to match statuses (booleans).
 */

/**
 * schema for DCPData
 * @private
 */
var DCPData = schema({
  aliases: [null, {
    '*': String,
  }],
  attributes: [null, {
    '*': { // datasource ID ->
      '*': [String, Number, Boolean], // attribute ID -> value
    },
  }],
  conditionValues: [null, Array.of(String)],
  conditionValuesResults: [null, {
    '*': Boolean,
  }],
});

/**
 * @typedef {Object} VisitorId
 * @property {string} randomId
 */

/**
 * schema for VisitorId
 * @private
 */
var VisitorId = schema({
  randomId: String,
});

/**
 * @typedef {Object} VisitorIdLocator
 * @property {string} type
 * @property {string} name
 */

/**
 * schema for VisitorIdLocator
 * @private
 */
var VisitorIdLocator = schema({
    type: String,
    name: String,
});

/**
 * A getter which can be passed to `di.evaluate`. An array of N elements, the first N-1 of which are strings which
 * define sources which are depended on and the last of which is a Function with N-1 arguments, each of which
 * corresponds to the source at that position (resolved at evaluate time).
 *
 * @typedef {Array.<String|Function>} Getter
 */

/**
 * schema for Getter
 * @private
 */
var Getter = schema(Array.of([String, Function]));

/**
 * @callback MatcherFn
 * @param {*} value
 * @param {Condition} cond
 * @return {Boolean}
 */

/**
 * schema for MatcherFn
 * TODO(tyler): can we declare the params?
 * @private
 */
var MatcherFn = schema(Function);

/**
 * @typedef {Array<String>} KeyPath
 */
 var KeyPath = schema(Array.of(1, Infinity, String));

/**
 * @callback FieldsNeededGetter
 * @param {Condition} cond Condition from which to extract fields
 * @return {Array.<String|KeyPath>} List of key paths
 */

/**
 * @typedef {Array<(String|KeyPath)>|FieldsNeededGetter} FieldsNeeded
 * FieldsNeeded is either
 *   - an array of 'shallow' keys (e.g., 'browserId') and/or 'deep'
 *     key paths (e.g., ['interestGroups', '12345', 'score']); OR
 *   - a function with signature (Condition) -> Array, where the array is like the aforementioned.
 */
 var FieldsNeeded = schema([Array.of([String, KeyPath]), Function]);

/**
 * @typedef {Object} AudienceMatcher
 * @property {FieldsNeeded} [fieldsNeeded] - If defined, field(s) from the
 *  `visitorProfile` that are required to be defined in order for the matcher to be invoked.
 *  In its Function variant, the function has signature (Condition) => Array<String>, returning
 *  concrete field names.
 * @property {MatcherFn} match - Function called to match using this Matcher
 */

/**
 * schema for AudienceMatcher
 * @private
 */
var AudienceMatcher = schema({
  fieldsNeeded: [null, FieldsNeeded],
  match: MatcherFn,
});

/**
 * @callback SyncProviderFn
 * @param {...*} sources - Sources depended on
 * @return {*} Value to add to `visitorProfile`
 */

/**
 * @callback AsyncProviderFn
 * @param {...*} sources - Sources depended on
 * @return {Promise|AsyncFunctionGetterFn} Promise that resolves to value to add to `visitorProfile`
 */


/**
 * @callback AsyncFunctionGetterFn
 * @param {Function} getValue - function to retrieve current provider value
 * @param {Function} setValue - function to set current provider value
 * @return {Promise} Promise that resolves to value to add to `visitorProfile`
 */

/**
 * A Getter-style array where the first n-1 elements are strings representing sources to depend on and the last element
 * is a SyncProviderFn or AsyncProviderFn with args defined by those sources.
 *
 * @typedef {Array.<(String|SyncProviderFn|AsyncProviderFn|AsyncFunctionGetterFn)>} ProviderGetter
 */

/**
 * @callback Restorer
 * @param {*} value Persisted value to be loaded
 * @returns {*} restoredValue Transformed value to load in existing visitor profile
 */

/**
 * @typedef {Object} VisitorProfileProvider
 * @property {string} provides - name of provided field in `visitorProfile`
 * @property {Boolean} [isAsync] - if true, expect `getter` to be an AsyncProviderFn; else expect SyncProviderFn
 * @property {Boolean} [isLazy] - if true, evaluate `getter` at layer activation time; else evaluate eagerly at
 * at client activation time.
 * @property {Boolean} [isSticky] - if true, and a value already exists in the `visitorProfile`, don't evaluate
 * the `getter` again.
 * @property {Boolean} [isTransient] - if true, don't include the provider's value in the stored `visitorProfile`.
 * @property {ProviderGetter} [getter] - getter called to get the value provided, or undefined if no getter needed
 * @property {Boolean} [shouldTrack] - whether to track the provided value(s) as userFeatures (default false)
 * @property {Restorer} [restorer] - function to call on stored value to load into existing visitor profile
 * @property {AttributionTypeSchema} [attributionType] - how to resolve conflicts across domains
 */

/**
 * schema for VisitorProfileProvider
 * @private
 */
var VisitorProfileProvider = schema({
  provides: String,
  isAsync: [null, Boolean],
  isLazy: [null, Boolean],
  isSticky: [null, Boolean],
  isTransient: [null, Boolean],
  getter: [null, Getter],
  shouldTrack: [null, Boolean],
  restorer: [null, Function],
  attributionType: [null, AttributionTypeSchema],
});

/**
 * @typedef {Object} EmitterEvent
 * @property {string} type Type ("namespace") of the emitted event
 * @property {string} name Name of the emitted event
 * @property {Object} [data] Optional data for the event payload
 */

/**
 * schema
 * @private
 */
var EmitterEvent = schema({
  type: String,
  name: String,
  data: [null, Object],
});

/**
 * @callback EventListenerFn
 * @param {EmitterEvent} event
 */

/**
 * @typedef {Object} EventListenerConfig
 * @property {EventListenerFn} handler
 * @property {Object} [filter] filter only certain event type/names from being handled
 * @property {string} [filter.type] filter by events having matching type
 * @property {string} [filter.name] filter by events having matching name
 * @property {string} [token] unique identifier that can be used to unlisten to event
 * @property {Boolean} [emitErrors] If true, errors in handler will be emitted as error events (default false)
 * @property {Boolean} [publicOnly] If true, listen to public events only (default false)
 */

/**
 * Schema
 * @private
 */
var EventListenerConfig = schema({
  handler: Function,
  filter: [null, {
    type: [null, String],
    name: [null, String],
  }],
  token: [null, String],
  emitErrors: [null, Boolean],
  publicOnly: [null, Boolean],
});

/**
 * @typedef {Object} LayerDecidedEventData
 * @property {Layer} layer - The layer that has been decided
 * @property {DecisionTicket} decisionTicket - The decision ticket for the decided layer decision.
 * @property {Decision} decision - The actual decision made for the layer decision event.
 */

/**
 * schema for LayerDecidedApiEventData
 * @private
 */
var LayerDecidedApiEventData = schema({
  layer: Layer,
  decisionTicket: DecisionTicket,
  decision: Decision,
  audiences: Array.of(Audience),
});

/**
 * @typedef {Object} CampaignDecidedEventData
 * @property {Campaign} campaign - The campaign that has been decided
 * @property {DecisionTicket} decisionTicket - The decision ticket for the decided campaign decision.
 * @property {CampaignDecision} decision - The actual decision made for the campaign decision event.
 */

/**
 * schema for CampaignDecidedApiEventData
 * @private
 */
var CampaignDecidedApiEventData = schema({
  campaign: Campaign,
  decisionTicket: DecisionTicket,
  decision: CampaignDecision,
  audiences: Array.of(Audience),
});

/**
 * @typedef {Object} LayerDecidedApiEvent
 * @property {string} type - The type of event.
 * @property {string} name - The name of the event.
 * @property {LayerDecidedEventData} data - The data for the layer that has been decided.
 */
var LayerDecidedApiEvent = schema({
  type: 'lifecycle',
  name: 'layerDecided',
  data: LayerDecidedApiEventData,
});

/**
 * @typedef {Object} CampaignDecidedApiEvent
 * @property {string} type - The type of event.
 * @property {string} name - The name of the event.
 * @property {CampaignDecidedEventData} data - The data for the layer that has been decided.
 */
var CampaignDecidedApiEvent = schema({
  type: 'lifecycle',
  name: 'campaignDecided',
  data: CampaignDecidedApiEventData,
});

/**
 * @typedef {Object} ViewActivatedEventData
 * @property {View} view - The view that has been activated.
 */

/**
 * schema for ViewActivatedEventData
 * @private
 */
var ViewActivatedApiEventData = schema({
  view: View,
});

/**
 * @typedef {Object} PageActivatedEventData
 * @property {Page} page - The page that has been activated.
 */

/**
 * schema for PageActivatedEventData
 * @private
 */
var PageActivatedApiEventData = schema({
  page: Page,
});

/**
 * @typedef {Object} PageDeactivatedApiEventData
 * @property {Page} page - The page that has been deactivated.
 */

/**
 * schema for PageDeactivatedApiEventData
 * @private
 */
var PageDeactivatedApiEventData = schema({
  page: Page,
});

/**
 * @typedef {Object} ViewsActivatedEventData
 * @property {View[]} views - The views that have been activated.
 */

/**
 * schema for ViewsActivatedEventData
 * @private
 */
var ViewsActivatedApiEventData = schema({
  views: Array.of([View]),
});


/**
 * @typedef {Object} ViewActivatedApiEvent
 * @property {string} type - The type of event.
 * @property {string} name - The name of the event.
 * @property {ViewActivatedEventData} data - The viewEventData for the view that has been activated.
 */

/**
 * schema
 * @private
 */
var ViewActivatedApiEvent = schema({
  type: 'lifecycle',
  name: 'viewActivated',
  data: ViewActivatedApiEventData,
});

/**
 * @typedef {Object} PageActivatedEvent
 * @property {string} type - The type of event.
 * @property {string} name - The name of the event.
 * @property {PageActivatedEventData} data - The pageEventData for the page that has been activated.
 */

/**
 * schema
 * @private
 */
var PageActivatedApiEvent = schema({
  type: 'lifecycle',
  name: 'pageActivated',
  data: PageActivatedApiEventData,
});

/**
 * @typedef {Object} PageDeactivatedApiEvent
 * @property {string} type - The type of event.
 * @property {string} name - The name of the event.
 * @property {PageDeactivatedApiEventData} data - The PageDeactivatedApiEventData for the view that has been deactivated.
 */

/**
 * schema
 * @private
 */
var PageDeactivatedApiEvent = schema({
  type: 'lifecycle',
  name: 'pageDeactivated',
  data: PageDeactivatedApiEventData,
});

/**
 * @typedef {Object} ViewsActivatedApiEvent
 * @property {string} type - The type of event.
 * @property {string} name - The name of the event.
 * @property {ViewsActivatedEventData} data - The views that have been activated.
 */

/**
 * schema
 * @private
 */
var ViewsActivatedApiEvent = schema({
  type: 'lifecycle',
  name: 'viewsActivated',
  data: ViewsActivatedApiEventData,
});

/**
 * @typedef {Object} FilteredChangeData - A Change with fields removed that should not be publicly exposed.
 * @property {string} type - Type of the change (e.g. "attribute", "code").
 * @property {string} [selector] - (For visual Element-based changes only) Selector chosen
 * in the Editor.
 * @property {Object} [attributes] - Change-specific attributes, such as the HTML attribute
 * to modify, and the modified value.
 * @property {string|Function} [value] - String value for "insert_html", "insert_image", and "custom_css".
 * Function to execute if "custom_code".
 */

/**
 * schema for FilteredChangeData
 * @private
 */
var FilteredChangeData = schema({
  type: String,
  selector: [null, String],
  attributes: [null, Object],
  value: [null, String, Function],
});

/**
 * A filtered ActionData where the changes in the changeSet are filtered.
 *
 * @typedef {Object} FilteredActionData
 * @property {string} id
 * @property {string} type
 * @property {string} layerId
 * @property {string} [experimentId]
 * @property {string} [variationId]
 * @property {string} [pageId]
 * @property {FilteredChangeData[]} changeSet
 */

/**
 * @private
 */
var FilteredActionData = schema({
  id: String,
  type: String,
  layerId: String,
  experimentId: [null, String],
  variationId: [null, String],
  pageId: [null, String],
  changeSet: Array.of([FilteredChangeData, []]),
});

/**
 * @typedef {Object} ActionAppliedEventData
 * @property {FilteredChangeData[]} changes - List of Changes that were attempted.
 * @property {string} campaignId - ID of Campaign the action belongs to.
 * @property {string} pageId - ID of the Page on which the action was executed.
 * @property {string} [experimentId] - ID of Experiment the action belongs to, or undefined if Campaign action.
 * @property {string} [variationId] - ID of Variation the action belongs to, or undefined if Campaign or Experiment action.
 */

/**
 * schema for ActionAppliedEventData
 * @private
 */
var ActionAppliedEventData = schema({
  changes: Array.of(FilteredChangeData),
  campaignId: String,
  pageId: [null, String],
  experimentId: [null, String],
  variationId: [null, String],
});

/**
 * @typedef {Object} ActionAppliedEvent
 * @property {string} type - The type of the Event. Always "action" for this Event.
 * @property {string} name - The name of the Event. Always "applied" for this Event.
 * @property {ActionAppliedEventData} data - Data about the Event.
 */
var ActionAppliedApiEvent = schema({
  type: 'action',
  name: 'applied',
  data: ActionAppliedEventData,
});

/**
 * @typedef {Object} ActionsForDecisionAppliedEventData
 * @property {Decision} decision
 * @property {ActionData[]} actions - List of actions for decision.
 */

/**
 * schema for ActionsForDecisionAppliedEventData
 * @private
 */
var ActionsForDecisionAppliedEventData = schema({
  actions: Array.of(FilteredActionData),
  decision: Decision,
});

/**
 * @typedef {Object} ActionsForDecisionAppliedApiEvent
 * @property {string} type - The type of the Event. Always "action" for this Event.
 * @property {string} name - The name of the Event. Always "appliedAllForDecision" for this Event.
 * @property {ActionsForDecisionAppliedEventData} data - Data about the Event.
 */
var ActionsForDecisionAppliedApiEvent = schema({
  type: 'action',
  name: 'appliedAllForDecision',
  data: ActionsForDecisionAppliedEventData,
});

/**
 * @typedef {Object} AnalyticsApiEventData
 * @property {string} name - The name of the event.
 * @property {string} type - The type of the event.
 * @property {string} category - The category for the event.
 * @property {ViewTag} tags - The list of tags associated with the event.
 * @property {Object} metrics - Any additional metrics for the event (such as revenue).
 */

/**
 * schema for AnalyticsApiEventData
 * @private
 */
var AnalyticsApiEventData = schema({
  name: String,
  type: String,
  category: String,
  tags: Object,
  metrics: Object,
});

/**
 * @typedef {Object} AnalyticsApiEvent
 * @property {string} type - The type of event.
 * @property {string} name - The name of the event.
 * @property {AnalyticsApiEventData} data - The data for the event that was triggered.
 */
var AnalyticsApiEvent = schema({
  type: 'analytics',
  name: 'trackEvent',
  data: AnalyticsApiEventData,
});

/**
 * @callback AttachEventFn
 * @param {EventSchema} event - event instance to register the attach javascript
 */

/**
 * @callback DetachEventFn
 * @param {EventSchema} event - event instance to register the detach javascript
 */

/**
 * @typedef {Object} EventImplementation
 * @property {AttachEventFn} attach
 * @property {DetachEventFn} detach
 */
var EventImplementation = schema({
  attach: Function,
  detach: Function,
});

/**
 * @typedef {Object} InitializedApiEvent
 * @property {string} type - The type of event.
 * @property {string} name - The name of the event.
 */
var InitializedApiEvent = schema({
  type: 'lifecycle',
  name: 'initialized',
});

/**
 * @typedef {Object} OriginsSyncedEvent
 * @property {string} type - The type of event.
 * @property {string} name - The name of the event.
 */
var OriginsSyncedEvent = schema({
  type: 'lifecycle',
  name: 'originsSynced',
});

/**
 * @typedef {Object} RumData
 * @property {string} id - RUM id
 * @property {string} v - RUM version
 * @property {string} account - account id
 * @property {string} project - project id
 * @property {string} [snippet] - snippet id
 * @property {string} revision - project revision
 * @property {Boolean} sync - JS loaded synchronously
 * @property {number} timebase - base to do measurements
 * @property {number} sampleRate - RUM sampling rate defined by config
 * @property {number} numBehaviorEvents - Number of Behavior Events
 * @property {string} clientVersion - Version of Client (only new client)
 * @property {Boolean} hasSlave - if __SLAVE__ is defined
 * @property {Boolean} wxhr
 * @property {Boolean} [activateDfd] - whether activation was deferred
 * @property {number} [render] - first frame after client load
 * @property {number} [connectStart] - Resourcetiming API data
 * @property {number} [connectEnd] - Resourcetiming API data
 * @property {number} [decodedBodySize] - Resourcetiming API data
 * @property {number} [domainLookupStart] - Resourcetiming API data
 * @property {number} [domainLookupEnd] - Resourcetiming API data
 * @property {number} [duration] - Resourcetiming API data
 * @property {number} [encodedBodySize] - Resourcetiming API data
 * @property {number} [fetchStart] - Resourcetiming API data
 * @property {number} [requestStart] - Resourcetiming API data
 * @property {number} [responseStart] - Resourcetiming API data
 * @property {number} [responseEnd] - Resourcetiming API data
 * @property {number} [secureConnectionStart] - Resourcetiming API data
 * @property {number} [startTime] - Resourcetiming API data
 * @property {number} [transferSize] - Resourcetiming API data
 */

/**
 * schema for RumData
 * @private
 */
var RumData = schema({
  id: String,
  v: String,
  account: String,
  project: String,
  snippet: [String, null],
  revision: String,
  sync: [Boolean, null],
  timebase: Number,
  sampleRate: Number,
  numBehaviorEvents: [Number, null],
  clientVersion: String,
  hasSlave: Boolean,
  wxhr: Boolean,
  activateDfd: [Boolean, null],
  render: [Number, null],
  connectStart: [Number, null],
  connectEnd: [Number, null],
  domainLookupStart: [Number, null],
  domainLookupEnd: [Number, null],
  duration: [Number, null],
  encodedBodySize: [Number, null],
  fetchStart: [Number, null],
  requestStart: [Number, null],
  responseStart: [Number, null],
  responseEnd: [Number, null],
  secureConnectionStart: [Number, null],
  startTime: [Number, null],
  transferSize: [Number, null],
});

/**
 * An array of [startTime, duration] for a mark
 * @typedef {Array.<Number>} CompactUserMark
 */

/**
 * schema
 * @private
 */
var CompactUserMark = Array.of(2, Number);

/**
 * Object of performance measures pointing to array of Usermarks
 * @typedef {Object.<CompactUserMark[]>} UserTiming
 */

/**
 * schema
 * @private
 */
var UserTiming = schema({
  '*': Array.of(CompactUserMark)
});

/**
 * RUM data to be sent on 'load' event. Requires the id to match the one in RumData
 * @typedef {Object} RumDataOnLoad
 * @property {string} id
 * @property {string} v
 * @property {string} project
 * @property {NavigationTiming} navigationTimings - Navigation timing data
 * @property {UserTiming} userTimings - User timing data
 * @property {LocalStorageMetric} lsMetrics - Size and length of localstorage
 */

/**
 * schema
 * @private
 */
var RumDataOnLoad = schema({
  id: String,
  v: String,
  project: String,
  lsMetrics: LocalStorageMetric,
  navigationTimings: NavigationTiming,
  userTimings: UserTiming,
});

/**
 * @typedef {Object} NavigationTiming
 * @property {number} [connectEnd]
 * @property {number} [connectStart]
 * @property {number} [domComplete]
 * @property {number} [domContentLoadedEventEnd]
 * @property {number} [domContentLoadedEventStart]
 * @property {number} [domInteractive]
 * @property {number} [domLoading]
 * @property {number} [domainLoookupEnd]
 * @property {number} [domainLookupStart]
 * @property {number} [fetchStart]
 * @property {number} [loadEventEnd]
 * @property {number} [loadEventStart]
 * @property {number} [navigationStart]
 * @property {number} [redirectEnd]
 * @property {number} [redirectStart]
 * @property {number} [requestStart]
 * @property {number} [responseEnd]
 * @property {number} [responseStart]
 * @property {number} [secureConnectionStart]
 * @property {number} [unloadEventEnd]
 * @property {number} [unloadEventStart]
 */

/**
 * schema
 * @private
 */
var NavigationTiming = schema({
 connectEnd: [Number, null],
 connectStart: [Number, null],
 domComplete: [Number, null],
 domContentLoadedEventEnd: [Number, null],
 domContentLoadedEventStart: [Number, null],
 domInteractive: [Number, null],
 domLoading: [Number, null],
 domainLoookupEnd: [Number, null],
 domainLookupStart: [Number, null],
 fetchStart: [Number, null],
 loadEventEnd: [Number, null],
 loadEventStart: [Number, null],
 navigationStart: [Number, null],
 redirectEnd: [Number, null],
 redirectStart: [Number, null],
 requestStart: [Number, null],
 responseEnd: [Number, null],
 responseStart: [Number, null],
 secureConnectionStart: [Number, null],
 unloadEventEnd: [Number, null],
 unloadEventStart: [Number, null],
});

/**
 * @typedef {Object} LocalStorageMetric
 * @property {number} numKeys
 * @property {number} sizeKeys
 * @property {number} sizeValues
 * @property {number} idCounts
 * @property {number} foreignOriginCount
 * @property {Object} byteTotals
 * @property {Object} byteTotalsByCategory
 */

/**
 * schema
 * @private
 */
var LocalStorageMetric = schema({
  numKeys: Number,
  sizeKeys: Number,
  sizeValues: Number,
  idCounts: Number,
  foreignOriginCount: Number,
  byteTotals: Object,
  byteTotalsByCategory: Object,
});

/**
 * @typedef {Object} DecisionTicketPayload
 * @property {string} c campaign_id
 * @property {string} [x] experiment_id
 * @property {string} [v] variation_id
 * @property {Boolean} h is_campaign_holdback
 * @property {Object} [m] metadata
 * @property {string} [m.cmab_uuid] CMAB UUID if this is a CMAB experiment decision
 */

/**
 * schema
 * @private
 */
var DecisionTicketPayload = schema({
  c: String,
  x: [String, null],
  v: [String, null],
  h: Boolean,
  m: {
    cmab_uuid: [String, null],
    // Add other metadata properties here if needed
  }
});

/**
 * @typedef {Object} EventTicketPayload
 * @property {string} [e] entity_id
 * @property {string} k key
 * @property {number} t timestamp
 * @property {string} u uuid
 * @property {Object} [a] tags
 * @property {Object} [p] properties
 * @property {number} [q] quantity
 * @property {number} [$] revenue
 * @property {number} [v] value
 * @property {string} [y] type
 */

/**
 * schema
 * @private
 */
var EventTicketPayload = schema({
  e: [null, String],
  k: [String],
  t: [Number],
  u: [String],
  a: [Object, null],
  p: [Object, null],
  q: [Number, null],
  $: [Number, null],
  v: [Number, null],
  y: [String, null],
});

/**
 * @typedef {Object} EventBatch
 * @property {string} account_id
 * @property {Boolean} [anonymize_ip]
 * @property {string} client_name
 * @property {string} client_version
 * @property {string} [project_id]
 * @property {string} [revision]
 * @property {BatchVisitor[]} visitors
 */

/**
 * @private
 */
var EventBatch = schema({
  account_id: String,
  anonymize_ip: [null, Boolean],
  client_name: String,
  client_version: String,
  project_id: [null, String],
  revision: [null, String],
  visitors: Array.of(BatchVisitor),
});

/**
 * @typedef {Object} BatchVisitor
 * @property {string} [session_id]
 * @property {string} visitor_id
 * @property {BatchVisitorAttribute[]} attributes
 * @property {BatchEventSnapshot[]} snapshots
 */

/**
 * @private
 */
var BatchVisitor = schema({
  session_id: [null, String],
  visitor_id: String,
  attributes: Array.of(BatchVisitorAttribute),
  snapshots: Array.of(BatchEventSnapshot),
});

/**
 * @typedef {Object} BatchVisitorAttribute
 * @property {number} [entity_id]
 * @property {string} [key]
 * @property {string} type
 * @property {string|Number|Boolean} value
 */

/**
 * @private
 */
var BatchVisitorAttribute = schema({
  entity_id: [null, Number],
  key: [null, String],
  type: String,
  value: [String, Number, Boolean],
});

/**
 * @typedef {Object} BatchEventSnapshot
 * @property {BatchDecisionTicket[]} decisions
 * @property {BatchEventTicket[]} events
 */

/**
 * @private
 */
var BatchEventSnapshot = schema({
  decisions: Array.of(BatchDecisionTicket),
  events: Array.of(BatchEventTicket),
});

/**
 * @typedef {Object} BatchDecisionTicket
 * @property {string} campaign_id
 * @property {string} [experiment_id]
 * @property {string} [variation_id]
 * @property {Boolean} is_campaign_holdback
 */

/**
 * @private
 */
var BatchDecisionTicket = schema({
  campaign_id: String,
  experiment_id: [null, String],
  variation_id: [null, String],
  is_campaign_holdback: Boolean,
});

/**
 * @typedef {Object} BatchEventTicket
 * @property {string} [entity_id]
 * @property {string} [key]
 * @property {number} timestamp
 * @property {string} uuid
 * @property {Object} [tags]
 * @property {number} [quantity]
 * @property {number} [revenue]
 * @property {number} [value]
 */

/**
 * @private
 */
var BatchEventTicket = schema({
  entity_id: [null, String],
  key: [null, String],
  timestamp: Number,
  uuid: String,
  tags: [null, Object],
  quantity: [null, Number],
  revenue: [null, Number],
  value: [null, Number],
});

/**
 * "EventBatch" payload definition for the /v1/events API
 *
 * Swagger (public): http://api.optimizely.com/swagger/v1/events.json
 *
 * @typedef {Object} EventBatchPayload
 * @property {string} account_id
 * @property {Boolean} [anonymize_ip]
 * @property {string} client_name
 * @property {string} client_version
 * @property {string} [project_id]
 * @property {VisitorPayload[]} visitors
 */

/**
 * schema
 * @private
 */
var EventBatchPayload = schema({
  account_id: [String],
  anonymize_ip: [Boolean, null],
  client_name: [String],
  client_version: [String],
  project_id: [String, null],
  enrich_decisions: [Boolean, null],
  visitors: Array.of(VisitorPayload)
});

/**
 * "Visitor" for EventBatch (https://github.com/optimizely/avro-schemas/blob/f46af15/event_batch/src/main/avro/EventBatch.avsc#L61)
 *
 * @typedef {Object} VisitorPayload
 * @property {string} [session_id]
 * @property {string} visitor_id
 * @property {VisitorAttributePayload[]} attributes
 * @property {EventSnapshotPayload[]} snapshots
 */

/**
 * schema
 * @private
 */
var VisitorPayload = schema({
  session_id: [String, null],
  visitor_id: [String],
  attributes: Array.of(VisitorAttributePayload),
  snapshots: Array.of(EventSnapshotPayload)
});

/**
 * "Attribute" for EventBatch (https://github.com/optimizely/avro-schemas/blob/f46af15/event_batch/src/main/avro/EventBatch.avsc#L13)
 *
 * @typedef {Object} VisitorAttributePayload
 * @property {number} [e] entity_id
 * @property {string} [k] key
 * @property {string} t type
 * @property {string|Number|Boolean} v value
 */

/**
 * schema
 * @private
 */
var VisitorAttributePayload = schema({
  e: [Number, null],
  k: [String, null],
  t: [String],
  v: [String, Number, Boolean, null]
});

/**
 * "Snapshot" for EventBatch (https://github.com/optimizely/avro-schemas/blob/f46af15/event_batch/src/main/avro/EventBatch.avsc#L52)
 *
 * @typedef {Object} EventSnapshotPayload
 * @property {number} activationTimestamp
 * @property {DecisionTicketPayload[]} decisions
 * @property {EventTicketPayload[]} events
 */

/**
 * schema
 * @private
 */
var EventSnapshotPayload = schema({
  activationTimestamp: Number,
  decisions: Array.of(DecisionTicketPayload),
  events: Array.of(EventTicketPayload),
});

/**
 * @typedef {Object} UserAgentData
 * @property {Object} browser
 * @property {string} browser.id
 * @property {string} browser.version
 * @property {Object} device
 * @property {string} device.model
 * @property {string} device.type
 * @property {Boolean} device.isMobile
 * @property {Object} platform
 * @property {string} platform.name
 * @property {string} platform.version
 */

/**
 * schema
 * @private
 */
var UserAgentData = schema({
  browser: {
    id: String,
    version: String,
  },
  device: {
    model: String,
    type: String,
    isMobile: Boolean,
  },
  platform: {
    name: String,
    version: String,
  },
});

/**
 * Schema definitions for above types. Call the named property on an object to check if the object is valid (boolean response).
 * If not valid, call with `{PropertyName}.errors` on the same object to see what the problems are.
 * See [js-schema](https://github.com/molnarg/js-schema/) for details.
 *
 * @type {Object}
 */
exports.schema = {
  ActionData: ActionData,
  ActionAppliedApiEvent: ActionAppliedApiEvent,
  ActionsForDecisionAppliedApiEvent: ActionsForDecisionAppliedApiEvent,
  AnalyticsApiEvent: AnalyticsApiEvent,
  AnalyticsTracker: AnalyticsTracker,
  AnalyticsTrackingHook: AnalyticsTrackingHook,
  Audience: Audience,
  AudienceMatcher: AudienceMatcher,
  BasisPoints: BasisPoints,
  RuleSpecV0_1: RuleSpecV0_1,
  RuleSpecV0_2: RuleSpecV0_2,
  BatchDecisionTicket: BatchDecisionTicket,
  BatchEventTicket: BatchEventTicket,
  BatchVisitor: BatchVisitor,
  BehaviorCompactEvent: BehaviorCompactEvent,
  BehaviorVerboseEvent: BehaviorVerboseEvent,
  BehaviorStoredEvents: BehaviorStoredEvents,
  BehaviorEventSpec: BehaviorEventSpec,
  Campaign: Campaign,
  CampaignDecidedApiEvent: CampaignDecidedApiEvent,
  CampaignDecision: CampaignDecision,
  CampaignExperiment: CampaignExperiment,
  CampaignVariation: CampaignVariation,
  Change: Change,
  ChangeConfig: ChangeConfig,
  ClickEventData: ClickEventData,
  Conditions: Conditions,
  CookieOptions: CookieOptions,
  ClientActivationEventData: ClientActivationEventData,
  ClientData: ClientData,
  CompactUserMark: CompactUserMark,
  CustomEventData: CustomEventData,
  DCPData: DCPData,
  DCPKeyfieldLocator: DCPKeyfieldLocator,
  VisitorAttributeDescriptor: VisitorAttributeDescriptor,
  VisitorAttribute: VisitorAttribute,
  DCPProjectRequestDescriptor: DCPProjectRequestDescriptor,
  DCPSnippetRequestDescriptor: DCPSnippetRequestDescriptor,
  Decision: Decision,
  DecisionData: DecisionData,
  DecisionTicket: DecisionTicket,
  DecisionTicketPayload: DecisionTicketPayload,
  Dimension: Dimension,
  EmitterEvent: EmitterEvent,
  Event: EventSchema,
  EventBatch: EventBatch,
  EventBatchPayload: EventBatchPayload,
  EventListenerConfig: EventListenerConfig,
  EventSnapshotPayload: EventSnapshotPayload,
  EventTicketPayload: EventTicketPayload,
  EventImplementation: EventImplementation,
  Experiment: Experiment,
  FeatureValue: FeatureValue,
  FieldsNeeded: FieldsNeeded,
  GeoLocation: GeoLocation,
  Group: Group,
  GroupDecision: GroupDecision,
  GroupDecisionTicket: GroupDecisionTicket,
  InitializedApiEvent: InitializedApiEvent,
  InterestGroup: InterestGroup,
  IPGeo: IPGeo,
  KeyPath: KeyPath,
  Layer: Layer,
  LayerDecidedApiEvent: LayerDecidedApiEvent,
  LayerState: LayerState,
  PersistableLayerState: PersistableLayerState,
  ListData: ListData,
  LogLevel: LogLevel,
  LocalStorageMetric: LocalStorageMetric,
  NavigationTiming: NavigationTiming,
  OriginsSyncedEvent: OriginsSyncedEvent,
  PageActivatedApiEvent: PageActivatedApiEvent,
  PageDeactivatedApiEvent: PageDeactivatedApiEvent,
  PersistableEventBatch: PersistableEventBatch,
  PersistableDecisionWithContext: PersistableDecisionWithContext,
  PreparedChange: PreparedChange,
  RumData: RumData,
  RumDataOnLoad: RumDataOnLoad,
  RuleSpec: RuleSpec,
  SendEventsApiEvent: SendEventsApiEvent,
  SessionState: SessionState,
  TagGroup: TagGroup,
  UserAgentData: UserAgentData,
  UserTiming: UserTiming,
  ValueType: ValueType,
  Variation: Variation,
  View: View,
  ViewActivatedApiEvent: ViewActivatedApiEvent,
  ViewsActivatedApiEvent: ViewsActivatedApiEvent,
  ViewEventData: ViewEventData,
  ViewState: ViewState,
  ViewTag: ViewTag,
  VisitorPayload: VisitorPayload,
  VisitorAttributePayload: VisitorAttributePayload,
  VisitorId: VisitorId,
  VisitorIdLocator: VisitorIdLocator,
  VisitorProfileProvider: VisitorProfileProvider,
  WeightDistribution: WeightDistribution,
};
